/**
 * SPA shell: login (BFF cookie) → accounts / PATs / MCP setup.
 * Invite accept flow (B3): /invite/accept?token=… stores token in
 * sessionStorage, preview pre-login, accept after session.
 * Cognito tokens never touch localStorage.
 */

import * as api from "./api.js";
import { mcpSetupConfig } from "./config.js";
import { clear, copyText, el, formatDate, withShowPassword } from "./dom.js";
import {
  ApiError,
  DEFAULT_FEATURE_FLAGS,
  isAdminRole,
  isLoginChallenge,
  scopesOf,
  type Account,
  type AdminFlag,
  type AdminInvite,
  type AdminUser,
  type AuditEvent,
  type FeatureFlags,
  type FlagStatus,
  type GoogleServicesPrefs,
  type InvitePreview,
  type InviteStatus,
  type McpClient,
  type MintedPat,
  type PatMeta,
  type PatScope,
  type UserProfile,
  type UserRole,
} from "./types.js";
import type { JoinRequestRow } from "./api.js";

/** sessionStorage key for pre-login invite token (never localStorage). */
export const INVITE_TOKEN_STORAGE_KEY = "inbox_invite_token";

type ChallengeState =
  | { kind: "NEW_PASSWORD_REQUIRED"; session: string; email: string }
  | { kind: "SOFTWARE_TOKEN_MFA"; session: string; email: string }
  | null;

/** Unauthenticated password-reset UI (SPA BFF → Cognito ForgotPassword). */
type ForgotPasswordState =
  | { step: "request" }
  | { step: "confirm"; email: string }
  | null;

/** Public self-signup. Password is not kept after the create-account request. */
type SignupState =
  | { step: "form" }
  | { step: "confirm"; email: string }
  | null;

type InviteFlowState = {
  /** Raw inbox_inv_… held only in memory + sessionStorage. */
  token: string | null;
  preview: InvitePreview | null;
  /** Accept succeeded this session. */
  accepted: boolean;
  /** Preview/accept in progress. */
  loading: boolean;
};

type ImapDraft = {
  address: string;
  appPassword: string;
  displayName: string;
  imapHost: string;
  imapPort: string;
  smtpHost: string;
  smtpPort: string;
  smtpSecure: boolean | null;
  maxMessageMb: string;
  /** Status line: lookup progress, or the checked host summary. */
  discoveryNote: string | null;
  /** Provider-specific setup step. */
  instructions: string | null;
  passwordLabel: string;
  /** Preset the last lookup chose. Null until a lookup finishes. */
  preset: string | null;
  /** The user edited Server settings, so lookup must not replace them. */
  hostsOverridden: boolean;
};

function emptyImapDraft(): ImapDraft {
  return {
    address: "",
    appPassword: "",
    displayName: "",
    imapHost: "",
    imapPort: "",
    smtpHost: "",
    smtpPort: "",
    smtpSecure: null,
    maxMessageMb: "",
    discoveryNote: null,
    instructions: null,
    passwordLabel: "Mailbox password",
    preset: null,
    hostsOverridden: false,
  };
}

type AppState = {
  user: UserProfile | null;
  challenge: ChallengeState;
  /** Forgot-password panel when signed out (not a Cognito login challenge). */
  forgotPassword: ForgotPasswordState;
  /** Create-account panel on the public homepage. */
  signup: SignupState;
  accounts: Account[];
  /** Account open in the edit panel (click row). */
  selectedAccountId: string | null;
  tokens: PatMeta[];
  /** MCP OAuth + PAT connections (Active clients list). */
  mcpClients: McpClient[];
  minted: MintedPat | null;
  features: FeatureFlags;
  /** Own audit events (GET /api/v1/audit). */
  auditEvents: AuditEvent[];
  auditNextCursor: string | null;
  /** @deprecated Activity is a full page now; kept false unused. */
  auditExpanded: boolean;
  /** Top-right profile dropdown open. */
  profileMenuOpen: boolean;
  /** Admin anomaly flags (GET /api/v1/admin/flags) — owner/admin only. */
  adminFlags: AdminFlag[];
  adminFlagsNextCursor: string | null;
  adminFlagsStatusFilter: FlagStatus | "all";
  /** Admin invites (GET /api/v1/admin/invites) — requires invites.productized. */
  adminInvites: AdminInvite[];
  adminInviteStatusFilter: InviteStatus | "all";
  /** One-time invite URL from create/resend (shown until dismissed/copied). */
  lastInviteLink: { email: string; inviteUrl: string } | null;
  /** Admin users (GET /api/v1/admin/users). */
  adminUsers: AdminUser[];
  /** Public join-the-test waitlist (admin). */
  joinRequests: JoinRequestRow[];
  joinRequestStatusFilter: "pending" | "invited" | "dismissed" | "all";
  /** Connect account dialog: gmail | imap | null. */
  accountDialog: "gmail" | "imap" | null;
  /** IMAP dialog fields, kept across the verify re-render. Not stored on disk. */
  imapDraft: ImapDraft | null;
  /** Ignores a late mail-discovery response after the address changed. */
  imapDiscoverSeq: number;
  loading: boolean;
  busy: string | null;
  error: string | null;
  notice: string | null;
  invite: InviteFlowState;
};

const state: AppState = {
  user: null,
  challenge: null,
  forgotPassword: null,
  signup: null,
  accounts: [],
  selectedAccountId: null,
  tokens: [],
  mcpClients: [],
  minted: null,
  features: { ...DEFAULT_FEATURE_FLAGS },
  auditEvents: [],
  auditNextCursor: null,
  auditExpanded: false,
  profileMenuOpen: false,
  adminFlags: [],
  adminFlagsNextCursor: null,
  adminFlagsStatusFilter: "open",
  adminInvites: [],
  adminInviteStatusFilter: "pending",
  lastInviteLink: null,
  adminUsers: [],
  joinRequests: [],
  joinRequestStatusFilter: "pending",
  accountDialog: null,
  imapDraft: null,
  imapDiscoverSeq: 0,
  loading: true,
  busy: null,
  error: null,
  notice: null,
  invite: {
    token: null,
    preview: null,
    accepted: false,
    loading: false,
  },
};

const AUDIT_PAGE_SIZE = 25;
const FLAGS_PAGE_SIZE = 25;

let root: HTMLElement;

export function mount(container: HTMLElement): void {
  root = container;
  if (typeof window !== "undefined") {
    window.addEventListener("popstate", () => {
      state.profileMenuOpen = false;
      render();
    });
  }
  wireProfileMenuDismiss();
  void boot();
}

/** Read OAuth callback / deep-link query on /accounts or /. */
function consumeAccountsQuery(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const connected = url.searchParams.get("connected");
    const err = url.searchParams.get("error");
    const errDesc = url.searchParams.get("error_description");
    if (connected === "1") {
      state.notice =
        "Gmail connected successfully. Service badges and tools update from the scopes Google granted.";
    } else if (err) {
      state.error =
        errDesc && errDesc.trim()
          ? `Gmail connect failed: ${errDesc}`
          : `Gmail connect failed (${err})`;
    }
    if (connected || err) {
      url.searchParams.delete("connected");
      url.searchParams.delete("error");
      url.searchParams.delete("error_description");
      const clean =
        url.pathname +
        (url.searchParams.toString() ? `?${url.searchParams}` : "") +
        url.hash;
      window.history.replaceState({}, "", clean || "/");
    }
  } catch {
    // ignore malformed URL
  }
}

/** `/?signup=1` (including the old /join redirect) opens create-account. */
function consumeSignupQuery(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("signup") !== "1") return;
    state.signup = { step: "form" };
    state.forgotPassword = null;
    url.searchParams.delete("signup");
    const clean =
      url.pathname +
      (url.searchParams.toString() ? `?${url.searchParams}` : "") +
      url.hash;
    window.history.replaceState({}, "", clean || "/");
  } catch {
    // ignore malformed URL
  }
}

function readSessionInviteToken(): string | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const t = sessionStorage.getItem(INVITE_TOKEN_STORAGE_KEY);
    return t && t.startsWith("inbox_inv_") ? t : null;
  } catch {
    return null;
  }
}

function writeSessionInviteToken(token: string): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(INVITE_TOKEN_STORAGE_KEY, token);
  } catch {
    // private mode / quota — keep in-memory only
  }
}

function clearSessionInviteToken(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Capture invite token from /invite/accept?token=… into sessionStorage,
 * strip token from the URL (avoid history leakage), keep path for UI.
 */
function consumeInviteQuery(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const path = url.pathname.replace(/\/+$/, "") || "/";
    const isAcceptPath =
      path === "/invite/accept" || path.endsWith("/invite/accept");
    const qToken = url.searchParams.get("token");
    if (qToken && qToken.startsWith("inbox_inv_")) {
      writeSessionInviteToken(qToken);
      state.invite.token = qToken;
      url.searchParams.delete("token");
      const cleanPath = isAcceptPath ? "/invite/accept" : path;
      const clean =
        cleanPath +
        (url.searchParams.toString() ? `?${url.searchParams}` : "") +
        url.hash;
      window.history.replaceState({}, "", clean);
    } else if (isAcceptPath || readSessionInviteToken()) {
      state.invite.token = readSessionInviteToken();
    }
  } catch {
    // ignore
  }
}

function isInviteAcceptRoute(): boolean {
  if (typeof window === "undefined") return !!state.invite.token;
  try {
    const path = new URL(window.location.href).pathname.replace(/\/+$/, "") || "/";
    return (
      path === "/invite/accept" ||
      path.endsWith("/invite/accept") ||
      !!state.invite.token
    );
  } catch {
    return !!state.invite.token;
  }
}

async function loadInvitePreview(): Promise<void> {
  const token = state.invite.token;
  if (!token) return;
  state.invite.loading = true;
  try {
    const preview = await api.previewInvite(token);
    state.invite.preview = preview;
  } catch (err) {
    state.invite.preview = null;
    setError(inviteErrorMessage(err));
  } finally {
    state.invite.loading = false;
  }
}

/**
 * After session login, bind JWT/session sub via POST accept.
 * Idempotent same-sub; mismatch → 401; expired → 410; other sub → 409.
 */
async function tryAcceptPendingInvite(): Promise<boolean> {
  const token = state.invite.token ?? readSessionInviteToken();
  if (!token || !state.user) return false;
  state.invite.token = token;
  setBusy("invite-accept");
  try {
    const res = await api.acceptInvite(token);
    if (res.status === "pending_login") {
      // Session missing on server — keep token for retry.
      return false;
    }
    state.invite.accepted = true;
    clearSessionInviteToken();
    state.invite.token = null;
    setNotice(`Invite accepted for ${res.email}. Welcome!`);
    setError(null);
    // Leave /invite/accept path for a clean dashboard URL.
    if (typeof window !== "undefined") {
      try {
        window.history.replaceState({}, "", "/");
      } catch {
        // ignore
      }
    }
    return true;
  } catch (err) {
    setError(inviteErrorMessage(err));
    return false;
  } finally {
    setBusy(null);
  }
}

function inviteErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return "Signed-in email does not match this invite. Sign out and use the invited address.";
    }
    if (err.status === 410) {
      return err.message || "This invite has expired or was revoked.";
    }
    if (err.status === 409) {
      return err.message || "This invite was already accepted by another account.";
    }
    if (err.status === 404) {
      return "Invite link is invalid or has already been used.";
    }
    return err.message || err.code;
  }
  return errMessage(err);
}

async function boot(): Promise<void> {
  state.loading = true;
  state.error = null;
  consumeAccountsQuery();
  consumeSignupQuery();
  consumeInviteQuery();
  render();

  // Pre-login preview (safe metadata) when we have a token.
  if (state.invite.token) {
    await loadInvitePreview();
  }

  try {
    const me = await api.getMe();
    state.user = me;
    if (state.invite.token) {
      await tryAcceptPendingInvite();
    }
    await refreshDashboard();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      state.user = null;
    } else {
      // Network / server down — show login; surface soft message
      state.user = null;
      if (!(err instanceof ApiError && err.status === 401)) {
        state.notice = errMessage(err);
      }
    }
  } finally {
    state.loading = false;
    render();
  }
}

async function refreshDashboard(): Promise<void> {
  const [accounts, tokens, mcpClients, features, auditPage] =
    await Promise.all([
      api.listAccounts(),
      api.listTokens(),
      api.listMcpClients().catch(() => [] as McpClient[]),
      api.tryGetFeatures(),
      api.listAudit({ limit: AUDIT_PAGE_SIZE }),
    ]);
  state.accounts = accounts;
  state.tokens = tokens;
  state.mcpClients = mcpClients;
  state.features = features;
  state.auditEvents = auditPage.events;
  state.auditNextCursor = auditPage.nextCursor ?? null;

  if (state.user && isAdminRole(state.user.role)) {
    await refreshAdminData();
  } else {
    state.adminFlags = [];
    state.adminFlagsNextCursor = null;
    state.adminInvites = [];
    state.adminUsers = [];
    state.joinRequests = [];
    state.lastInviteLink = null;
  }
}

/** Load admin-only lists (invites, users, join requests, flags). Soft-fails per section. */
async function refreshAdminData(): Promise<void> {
  // Users always available to admin (not gated by invites.productized).
  try {
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    state.adminUsers = [];
    if (err instanceof ApiError && err.status !== 403) {
      setNotice(`Admin users unavailable: ${errMessage(err)}`);
    }
  }

  try {
    const status =
      state.joinRequestStatusFilter === "all"
        ? undefined
        : state.joinRequestStatusFilter;
    state.joinRequests = await api.listJoinRequests(
      status ? { status } : undefined,
    );
  } catch (err) {
    state.joinRequests = [];
    if (err instanceof ApiError && err.status !== 403) {
      setNotice(`Join requests unavailable: ${errMessage(err)}`);
    }
  }

  // Invites require invites.productized.
  if (state.features["invites.productized"]) {
    try {
      state.adminInvites = await api.listAdminInvites();
    } catch (err) {
      state.adminInvites = [];
      if (err instanceof ApiError && err.status !== 403) {
        setNotice(`Admin invites unavailable: ${errMessage(err)}`);
      }
    }
  } else {
    state.adminInvites = [];
  }

  try {
    const status =
      state.adminFlagsStatusFilter === "all"
        ? undefined
        : state.adminFlagsStatusFilter;
    const flagsPage = await api.listAdminFlags({
      limit: FLAGS_PAGE_SIZE,
      ...(status ? { status } : {}),
    });
    state.adminFlags = flagsPage.flags;
    state.adminFlagsNextCursor = flagsPage.nextCursor ?? null;
  } catch (err) {
    state.adminFlags = [];
    state.adminFlagsNextCursor = null;
    if (err instanceof ApiError && err.status !== 403) {
      setNotice(`Admin flags unavailable: ${errMessage(err)}`);
    }
  }
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.message || err.code;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

function setBusy(key: string | null): void {
  state.busy = key;
  render();
}

function setError(msg: string | null): void {
  state.error = msg;
}

function setNotice(msg: string | null): void {
  state.notice = msg;
}

function currentPath(): string {
  if (typeof window === "undefined") return "/";
  try {
    return new URL(window.location.href).pathname.replace(/\/+$/, "") || "/";
  } catch {
    return "/";
  }
}

/** Client-side admin dashboard route. */
export function isAdminPath(
  pathname: string = typeof window !== "undefined"
    ? window.location.pathname
    : "/",
): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return p === "/admin";
}

function isProfileRoute(): boolean {
  return currentPath() === "/profile";
}

function isActivityRoute(): boolean {
  return currentPath() === "/activity";
}

function navigateTo(path: string): void {
  if (typeof window === "undefined") return;
  state.profileMenuOpen = false;
  const next = path.startsWith("/") ? path : `/${path}`;
  if (window.location.pathname !== next) {
    window.history.pushState({}, "", next);
  }
  render();
}

let profileMenuDismissWired = false;
function wireProfileMenuDismiss(): void {
  if (profileMenuDismissWired || typeof document === "undefined") return;
  profileMenuDismissWired = true;
  document.addEventListener("click", (ev) => {
    if (!state.profileMenuOpen) return;
    const t = ev.target;
    if (!(t instanceof Node)) return;
    const menu = document.querySelector(".profile-menu");
    if (menu && menu.contains(t)) return;
    state.profileMenuOpen = false;
    render();
  });
  document.addEventListener("keydown", (ev) => {
    if (!state.profileMenuOpen) return;
    if (ev.key === "Escape") {
      state.profileMenuOpen = false;
      render();
    }
  });
}

// ── Render ────────────────────────────────────────────────────────────────

function render(): void {
  clear(root);
  if (state.loading) {
    root.append(
      el(
        "div",
        { className: "boot" },
        el("div", { className: "spinner" }),
        el("div", null, "Checking session…"),
      ),
    );
    return;
  }
  if (!state.user) {
    // Not signed in: /admin still needs login first.
    root.append(renderLogin());
    return;
  }
  if (isAdminPath()) {
    root.append(renderAdminShell());
    return;
  }
  root.append(renderShell());
}

function logoMark(): HTMLElement {
  return el("img", {
    className: "logo-mark",
    src: "/logo.svg",
    alt: "",
    width: "28",
    height: "28",
  });
}

function legalLinks(): HTMLElement {
  return el(
    "p",
    { className: "legal-links" },
    el("a", { href: "/privacy" }, "Privacy"),
    el("a", { href: "/terms" }, "Terms"),
  );
}

function renderLogin(): HTMLElement {
  const focused =
    state.challenge != null ||
    state.forgotPassword != null ||
    isInviteAcceptRoute();
  if (!focused) return renderPublicHome();

  const wrap = el("div", { className: "login-wrap" });
  const card = el("div", { className: "login-card" });

  card.append(
    el(
      "a",
      { className: "brand brand-link", href: "/" },
      logoMark(),
      el("span", null, "MCP to Email"),
    ),
  );

  if (state.challenge?.kind === "NEW_PASSWORD_REQUIRED") {
    card.append(
      el("h1", null, "Set a new password"),
      el(
        "p",
        { className: "lede" },
        "Your temporary password must be replaced before you can continue.",
      ),
    );
    if (isInviteAcceptRoute() && state.invite.preview) {
      card.append(inviteBanner());
    }
    if (state.error) card.append(alertBox("error", state.error));
    card.append(newPasswordForm(state.challenge.email, state.challenge.session));
  } else if (state.challenge?.kind === "SOFTWARE_TOKEN_MFA") {
    card.append(
      el("h1", null, "Two-factor authentication"),
      el("p", { className: "lede" }, "Enter the code from your authenticator app."),
    );
    if (state.error) card.append(alertBox("error", state.error));
    card.append(totpForm(state.challenge.email, state.challenge.session));
  } else if (state.forgotPassword?.step === "request") {
    card.append(
      el("h1", null, "Forgot password"),
      el(
        "p",
        { className: "lede" },
        "Enter your account email. We will email either a temporary password (first login) or a short verification code.",
      ),
    );
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    card.append(forgotPasswordRequestForm());
  } else if (state.forgotPassword?.step === "confirm") {
    card.append(
      el("h1", null, "Reset password"),
      el(
        "p",
        { className: "lede" },
        `Enter the short verification code we emailed to ${state.forgotPassword.email} and choose a new password. (If the email said “temporary password”, go back to Sign in instead.)`,
      ),
    );
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    card.append(forgotPasswordConfirmForm(state.forgotPassword.email));
  } else if (isInviteAcceptRoute()) {
    card.append(
      el("h1", null, "Accept your invite"),
      el(
        "p",
        { className: "lede" },
        "Sign in with the invited email. After login we bind your account to this invite.",
      ),
    );
    card.append(inviteBanner());
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    if (state.invite.loading) {
      card.append(el("p", { className: "hint" }, "Checking invite…"));
    } else if (state.invite.token) {
      card.append(loginForm({ prefillEmail: state.invite.preview?.email }));
    } else {
      card.append(
        alertBox("error", "Missing invite token. Open the full link from your email."),
      );
    }
  }

  card.append(legalLinks());
  wrap.append(card);
  return wrap;
}

function renderPublicHome(): HTMLElement {
  const page = el("div", { className: "home" });
  const bar = el("header", { className: "home-bar" });
  bar.append(
    el(
      "a",
      { className: "brand brand-link", href: "/" },
      logoMark(),
      el("span", null, "MCP to Email"),
    ),
    el(
      "nav",
      { className: "home-nav" },
      el("a", { href: "/privacy" }, "Privacy"),
      el("a", { href: "/terms" }, "Terms"),
    ),
  );

  const story = el("div", { className: "home-story" });
  story.append(
    el(
      "div",
      { className: "mark-rules", "aria-hidden": "true" },
      el("span"),
      el("span"),
      el("span"),
    ),
    el(
      "h1",
      null,
      "Your mailbox, on the assistant you already use.",
    ),
    el(
      "p",
      { className: "home-lede" },
      "MCP to Email connects a Gmail account you choose to an AI client over MCP. The client can read, draft, and send mail. Drive, Docs, Sheets, Slides, Calendar, Contacts, Tasks, and Meet are included only if you leave them checked.",
    ),
  );

  const caps = el("dl", { className: "home-capabilities" });
  const rows: Array<[string, string]> = [
    ["Mail", "Search, read, label, draft, and send. Delete moves a message to trash."],
    ["Files", "Drive, Docs, Sheets, and Slides, when those services stay on."],
    ["The rest", "Calendar, contacts, tasks, and Meet. Each one follows its own checkbox."],
  ];
  for (const [term, detail] of rows) {
    caps.append(el("dt", null, term), el("dd", null, detail));
  }
  story.append(caps);

  const sheet = el("section", { className: "login-card home-sheet" });
  if (state.signup?.step === "confirm") {
    sheet.append(
      el("h2", null, "Confirm your email"),
      el(
        "p",
        { className: "lede" },
        `Enter the code we sent to ${state.signup.email}.`,
      ),
    );
  } else if (state.signup?.step === "form") {
    sheet.append(
      el("h2", null, "Create an account"),
      el(
        "p",
        { className: "lede" },
        "Use an email you can open. We will send a confirmation code.",
      ),
    );
  } else {
    sheet.append(
      el("h2", null, "Sign in"),
      el(
        "p",
        { className: "lede" },
        "Use the email and password on your account.",
      ),
    );
  }
  if (state.notice) sheet.append(alertBox("info", state.notice));
  if (state.error) sheet.append(alertBox("error", state.error));
  if (state.signup?.step === "confirm") {
    sheet.append(confirmSignupForm(state.signup.email));
  } else if (state.signup?.step === "form") {
    sheet.append(signupForm());
  } else {
    sheet.append(loginForm());
    sheet.append(
      el(
        "p",
        { className: "hint join-signin-link" },
        "New here? ",
        el("a", { href: "/?signup=1", id: "open-signup" }, "Create an account"),
        ".",
      ),
    );
  }
  sheet.append(
    el(
      "p",
      { className: "home-fineprint" },
      "The ",
      el("a", { href: "/terms" }, "terms"),
      " and ",
      el("a", { href: "/privacy" }, "privacy policy"),
      " apply when you use the service.",
    ),
  );
  sheet.querySelector("#open-signup")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    openSignupForm();
  });

  const main = el("main", { className: "home-main" });
  main.append(story, sheet);

  const foot = el(
    "footer",
    { className: "home-foot" },
    el("span", null, "mcpto.email"),
    el(
      "a",
      { href: "mailto:mcptoco@gmail.com" },
      "mcptoco@gmail.com",
    ),
  );

  page.append(bar, main, foot);
  return page;
}

function inviteBanner(): HTMLElement {
  const box = el("div", {
    className: "invite-banner",
    role: "status",
  });
  const preview = state.invite.preview;
  if (preview) {
    box.append(
      el(
        "div",
        { className: "invite-banner-title" },
        "Invite for ",
        el("strong", null, preview.email),
      ),
      el(
        "p",
        { className: "hint" },
        [
          preview.role ? `Role: ${preview.role}` : null,
          preview.expiresAt
            ? `Expires ${formatDate(preview.expiresAt)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ") || "Sign in to accept.",
      ),
    );
  } else if (state.invite.token && !state.invite.loading && !state.error) {
    box.append(el("p", { className: "hint" }, "Invite token ready — sign in to accept."));
  }
  return box;
}

function loginForm(opts?: { prefillEmail?: string }): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const email = el("input", {
    type: "email",
    name: "email",
    autocomplete: "username",
    required: true,
    placeholder: "you@example.com",
    value: opts?.prefillEmail ?? "",
  }) as HTMLInputElement;
  const password = el("input", {
    type: "password",
    name: "password",
    autocomplete: "current-password",
    required: true,
    placeholder: "Password",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Email", email),
    el("label", { className: "field" }, "Password", withShowPassword(password)),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "login" ? true : undefined,
        },
        state.busy === "login"
          ? "Signing in…"
          : isInviteAcceptRoute()
            ? "Sign in & accept invite"
            : "Sign in",
      ),
      el(
        "button",
        {
          type: "button",
          className: "btn-link",
        },
        "Forgot password?",
      ),
    ),
  );

  form.querySelector(".btn-link")?.addEventListener("click", () => {
    state.forgotPassword = { step: "request" };
    state.signup = null;
    state.challenge = null;
    setError(null);
    setNotice(null);
    render();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onLogin(email.value.trim(), password.value);
  });
  return form;
}

const SIGNUP_PASSWORD_RULE =
  "Use at least 12 characters, with an uppercase letter, a lowercase letter, a number, and a symbol.";

function passwordMeetsPolicy(password: string): boolean {
  return (
    password.length >= 12 &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[^A-Za-z0-9]/.test(password)
  );
}

function openSignupForm(): void {
  state.signup = { step: "form" };
  state.forgotPassword = null;
  state.challenge = null;
  setError(null);
  setNotice(null);
  render();
}

function signupForm(): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const email = el("input", {
    type: "email",
    name: "email",
    autocomplete: "username",
    required: true,
    placeholder: "you@example.com",
  }) as HTMLInputElement;
  const password = el("input", {
    type: "password",
    name: "password",
    autocomplete: "new-password",
    required: true,
    placeholder: "Password",
    minlength: "12",
  }) as HTMLInputElement;
  const confirm = el("input", {
    type: "password",
    name: "confirm",
    autocomplete: "new-password",
    required: true,
    placeholder: "Confirm password",
  }) as HTMLInputElement;
  const busy = state.busy === "signup";

  form.append(
    el("label", { className: "field" }, "Email", email),
    el("label", { className: "field" }, "Password", withShowPassword(password)),
    el("label", { className: "field" }, "Confirm password", withShowPassword(confirm)),
    el("p", { className: "hint" }, SIGNUP_PASSWORD_RULE),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: busy ? true : undefined,
        },
        busy ? "Creating account…" : "Create account",
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost" },
        "Already have an account? Sign in",
      ),
    ),
  );

  form.querySelector(".btn-ghost")?.addEventListener("click", () => {
    state.signup = null;
    setError(null);
    setNotice(null);
    render();
  });
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onSignup(email.value.trim(), password.value, confirm.value);
  });
  return form;
}

function confirmSignupForm(accountEmail: string): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const code = el("input", {
    type: "text",
    name: "code",
    inputmode: "numeric",
    autocomplete: "one-time-code",
    required: true,
    placeholder: "Confirmation code",
  }) as HTMLInputElement;
  const busy = state.busy === "signup" || state.busy === "signup-resend";

  form.append(
    el(
      "label",
      { className: "field" },
      "Account",
      el("input", {
        type: "email",
        value: accountEmail,
        disabled: true,
        readonly: true,
      }),
    ),
    el("label", { className: "field" }, "Confirmation code", code),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: busy ? true : undefined,
        },
        state.busy === "signup" ? "Confirming…" : "Confirm email",
      ),
      el(
        "button",
        {
          type: "button",
          className: "btn-ghost",
          "data-action": "resend",
          disabled: busy ? true : undefined,
        },
        state.busy === "signup-resend" ? "Sending…" : "Resend code",
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost", "data-action": "back" },
        "Back to sign in",
      ),
    ),
  );

  form
    .querySelector('[data-action="back"]')
    ?.addEventListener("click", () => {
      state.signup = null;
      setError(null);
      setNotice(null);
      render();
    });
  form
    .querySelector('[data-action="resend"]')
    ?.addEventListener("click", () => {
      void onResendSignup(accountEmail);
    });
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onConfirmSignup(accountEmail, code.value.trim());
  });
  return form;
}

function forgotPasswordRequestForm(): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const email = el("input", {
    type: "email",
    name: "email",
    autocomplete: "username",
    required: true,
    placeholder: "you@example.com",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Email", email),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "forgot" ? true : undefined,
        },
        state.busy === "forgot" ? "Sending…" : "Send reset code",
      ),
      el("button", { type: "button", className: "btn-ghost" }, "Back to sign in"),
    ),
  );

  form.querySelector(".btn-ghost")?.addEventListener("click", () => {
    state.forgotPassword = null;
    setError(null);
    setNotice(null);
    render();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onForgotPasswordRequest(email.value.trim());
  });
  return form;
}

function forgotPasswordConfirmForm(accountEmail: string): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const code = el("input", {
    type: "text",
    name: "code",
    inputmode: "numeric",
    autocomplete: "one-time-code",
    required: true,
    placeholder: "Verification code",
  }) as HTMLInputElement;
  const pw = el("input", {
    type: "password",
    name: "newPassword",
    autocomplete: "new-password",
    required: true,
    placeholder: "New password",
    minlength: "12",
  }) as HTMLInputElement;
  const confirm = el("input", {
    type: "password",
    name: "confirm",
    autocomplete: "new-password",
    required: true,
    placeholder: "Confirm new password",
  }) as HTMLInputElement;

  form.append(
    el(
      "label",
      { className: "field" },
      "Account",
      el("input", {
        type: "email",
        value: accountEmail,
        disabled: true,
        readonly: true,
      }),
    ),
    el("label", { className: "field" }, "Verification code", code),
    el("label", { className: "field" }, "New password", withShowPassword(pw)),
    el("label", { className: "field" }, "Confirm", withShowPassword(confirm)),
    el(
      "p",
      { className: "hint" },
      "Use at least 12 characters with upper, lower, number, and symbol (pool policy).",
    ),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "forgot" ? true : undefined,
        },
        state.busy === "forgot" ? "Updating…" : "Update password",
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost", "data-action": "resend" },
        "Resend code",
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost", "data-action": "back" },
        "Back to sign in",
      ),
    ),
  );

  form
    .querySelector('[data-action="back"]')
    ?.addEventListener("click", () => {
      state.forgotPassword = null;
      setError(null);
      setNotice(null);
      render();
    });
  form
    .querySelector('[data-action="resend"]')
    ?.addEventListener("click", () => {
      void onForgotPasswordRequest(accountEmail);
    });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    if (pw.value !== confirm.value) {
      setError("Passwords do not match");
      render();
      return;
    }
    void onForgotPasswordConfirm(accountEmail, code.value.trim(), pw.value);
  });
  return form;
}

function newPasswordForm(email: string, session: string): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const pw = el("input", {
    type: "password",
    name: "newPassword",
    autocomplete: "new-password",
    required: true,
    placeholder: "New password",
    minlength: "8",
  }) as HTMLInputElement;
  const confirm = el("input", {
    type: "password",
    name: "confirm",
    autocomplete: "new-password",
    required: true,
    placeholder: "Confirm new password",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Account", el("input", {
      type: "email",
      value: email,
      disabled: true,
      readonly: true,
    })),
    el("label", { className: "field" }, "New password", withShowPassword(pw)),
    el("label", { className: "field" }, "Confirm", withShowPassword(confirm)),
    el("p", { className: "hint" }, "Use at least 8 characters. Avoid reusing passwords."),
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "login" ? true : undefined,
        },
        state.busy === "login" ? "Saving…" : "Set password & sign in",
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost" },
        "Back",
      ),
    ),
  );

  form.querySelector(".btn-ghost")?.addEventListener("click", () => {
    state.challenge = null;
    setError(null);
    render();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    if (pw.value !== confirm.value) {
      setError("Passwords do not match");
      render();
      return;
    }
    void onNewPassword(email, session, pw.value);
  });
  return form;
}

function totpForm(email: string, session: string): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const code = el("input", {
    type: "text",
    name: "totpCode",
    inputmode: "numeric",
    autocomplete: "one-time-code",
    required: true,
    placeholder: "6-digit code",
    maxlength: "8",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Authenticator code", code),
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "login" ? true : undefined,
        },
        state.busy === "login" ? "Verifying…" : "Verify",
      ),
      el("button", { type: "button", className: "btn-ghost" }, "Back"),
    ),
  );

  form.querySelector(".btn-ghost")?.addEventListener("click", () => {
    state.challenge = null;
    setError(null);
    render();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onTotp(email, session, code.value.trim());
  });
  return form;
}

function profileInitials(user: UserProfile): string {
  const name = (user.displayName ?? "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (
        (parts[0]![0] ?? "") + (parts[parts.length - 1]![0] ?? "")
      ).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const email = user.email || "?";
  return email.slice(0, 2).toUpperCase();
}

/** Top-right profile control + dropdown (mirrors mcptoai). */
function profileMenuButton(): HTMLElement {
  const user = state.user!;
  const wrap = el("div", { className: "profile-menu" });

  const trigger = el(
    "button",
    {
      type: "button",
      className: "profile-menu-trigger",
      "aria-haspopup": "menu",
      "aria-expanded": state.profileMenuOpen ? "true" : "false",
      title: user.email,
    },
    el("span", { className: "profile-avatar" }, profileInitials(user)),
    el("span", { className: "profile-trigger-label" }, "Profile"),
  ) as HTMLButtonElement;
  trigger.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    state.profileMenuOpen = !state.profileMenuOpen;
    render();
  });
  wrap.append(trigger);

  if (!state.profileMenuOpen) return wrap;

  const menu = el("div", {
    className: "profile-menu-dropdown",
    role: "menu",
  });
  menu.append(
    el(
      "div",
      { className: "profile-menu-header" },
      el(
        "div",
        { className: "profile-menu-name" },
        user.displayName?.trim() || user.email,
      ),
      el("div", { className: "profile-menu-email" }, user.email),
    ),
  );

  const item = (
    label: string,
    onClick: () => void,
    opts?: { danger?: boolean },
  ): HTMLButtonElement => {
    const btn = el(
      "button",
      {
        type: "button",
        className: opts?.danger
          ? "profile-menu-item profile-menu-item-danger"
          : "profile-menu-item",
        role: "menuitem",
      },
      label,
    ) as HTMLButtonElement;
    btn.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      onClick();
    });
    return btn;
  };

  menu.append(
    item("Edit profile", () => navigateTo("/profile")),
    item("Activity log", () => navigateTo("/activity")),
  );

  if (state.features["docs.portal"]) {
    menu.append(
      item("Setup docs", () => {
        state.profileMenuOpen = false;
        try {
          window.location.href = "/docs";
        } catch {
          // ignore
        }
      }),
    );
  }

  if (isAdminRole(user.role)) {
    menu.append(
      item("Admin", () => {
        navigateTo("/admin");
      }),
    );
  }

  menu.append(el("div", { className: "profile-menu-sep" }));
  menu.append(
    item(
      state.busy === "logout" ? "Signing out…" : "Sign out",
      () => {
        void onLogout();
      },
      { danger: true },
    ),
  );

  wrap.append(menu);
  return wrap;
}

function renderTopbar(opts: { adminPage?: boolean } = {}): HTMLElement {
  const top = el("header", { className: "topbar" });
  const brand = el(
    "a",
    {
      href: "/",
      className: "brand brand-link",
      title: "MCP to Email",
    },
    logoMark(),
    el("span", null, "MCP to Email"),
  );
  brand.addEventListener("click", (ev) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navigateTo("/");
  });
  if (opts.adminPage) {
    brand.append(el("span", { className: "brand-admin-badge" }, "Admin"));
  }

  const meta = el("div", { className: "user-meta" });
  meta.append(profileMenuButton());
  top.append(brand, meta);
  return top;
}

function renderShell(): HTMLElement {
  const shell = el("div", { className: "app-shell" });
  shell.append(renderTopbar());

  if (state.error) shell.append(alertBox("error", state.error));
  if (state.notice) shell.append(alertBox("ok", state.notice));

  // Pending invite while already signed in (e.g. opened link after login).
  if (state.invite.token && !state.invite.accepted) {
    shell.append(pendingInvitePanel());
  }

  if (isProfileRoute()) {
    shell.append(profilePanel());
    return shell;
  }

  if (isActivityRoute()) {
    shell.append(auditPanel());
    return shell;
  }

  shell.append(accountsPanel());
  shell.append(mcpAccessPanel());

  if (state.accountDialog) {
    shell.append(accountConnectDialog(state.accountDialog));
  }

  return shell;
}

/** Profile page: display name + change password. */
function profilePanel(): HTMLElement {
  const wrap = el("div", { className: "profile-page" });
  wrap.append(profileIdentityPanel());
  wrap.append(profilePasswordPanel());
  wrap.append(profileDeletePanel());
  return wrap;
}

function profileIdentityPanel(): HTMLElement {
  const user = state.user!;
  const panel = el("section", { className: "panel" });
  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Edit profile"),
        el(
          "p",
          { className: "sub" },
          "Update how your name appears in the app. Email is your sign-in identity.",
        ),
      ),
    ),
  );

  const form = el("form", { className: "profile-form" }) as HTMLFormElement;
  const nameInput = el("input", {
    type: "text",
    className: "input",
    name: "displayName",
    maxlength: "80",
    autocomplete: "name",
    placeholder: "Display name",
    value: user.displayName ?? "",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Display name", nameInput),
    el(
      "label",
      { className: "field" },
      "Email",
      el("input", {
        type: "email",
        className: "input",
        value: user.email,
        disabled: "true",
        readonly: "true",
      }),
      el("span", { className: "hint" }, "Email cannot be changed here."),
    ),
    el(
      "label",
      { className: "field" },
      "Role",
      el("input", {
        type: "text",
        className: "input",
        value: user.role,
        disabled: "true",
        readonly: "true",
      }),
    ),
  );

  const actions = el("div", { className: "row-actions" });
  const saveBtn = el(
    "button",
    {
      type: "submit",
      className: "btn-primary btn-sm",
      disabled: state.busy === "profile-save" ? true : undefined,
    },
    state.busy === "profile-save" ? "Saving…" : "Save",
  ) as HTMLButtonElement;
  actions.append(saveBtn);
  form.append(actions);

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void (async () => {
      setBusy("profile-save");
      setError(null);
      setNotice(null);
      render();
      try {
        const updated = await api.updateProfile({
          displayName: nameInput.value,
        });
        state.user = { ...state.user!, ...updated };
        setNotice("Profile updated.");
      } catch (err) {
        setError(errMessage(err));
      } finally {
        setBusy(null);
        render();
      }
    })();
  });

  panel.append(form);
  return panel;
}

function profilePasswordPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Change password"),
        el(
          "p",
          { className: "sub" },
          "Use your current password, then choose a new one (min 12 characters, with upper, lower, number, and symbol).",
        ),
      ),
    ),
  );

  const form = el("form", { className: "profile-form" }) as HTMLFormElement;
  const currentIn = el("input", {
    type: "password",
    className: "input",
    name: "currentPassword",
    autocomplete: "current-password",
    placeholder: "Current password",
  }) as HTMLInputElement;
  const newIn = el("input", {
    type: "password",
    className: "input",
    name: "newPassword",
    autocomplete: "new-password",
    placeholder: "New password",
    minlength: "12",
  }) as HTMLInputElement;
  const confirmIn = el("input", {
    type: "password",
    className: "input",
    name: "confirmPassword",
    autocomplete: "new-password",
    placeholder: "Confirm new password",
    minlength: "12",
  }) as HTMLInputElement;

  form.append(
    el(
      "label",
      { className: "field" },
      "Current password",
      withShowPassword(currentIn),
    ),
    el(
      "label",
      { className: "field" },
      "New password",
      withShowPassword(newIn),
    ),
    el(
      "label",
      { className: "field" },
      "Confirm new password",
      withShowPassword(confirmIn),
    ),
  );

  const actions = el("div", { className: "row-actions" });
  const saveBtn = el(
    "button",
    {
      type: "submit",
      className: "btn-primary btn-sm",
      disabled: state.busy === "password-change" ? true : undefined,
    },
    state.busy === "password-change" ? "Updating…" : "Update password",
  ) as HTMLButtonElement;
  actions.append(saveBtn);
  form.append(actions);

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const previousPassword = currentIn.value;
    const newPassword = newIn.value;
    const confirm = confirmIn.value;
    if (!previousPassword || !newPassword) {
      setError("Current and new password are required.");
      render();
      return;
    }
    if (newPassword !== confirm) {
      setError("New password and confirmation do not match.");
      render();
      return;
    }
    if (newPassword.length < 12) {
      setError("New password must be at least 12 characters.");
      render();
      return;
    }
    void (async () => {
      setBusy("password-change");
      setError(null);
      setNotice(null);
      render();
      try {
        const res = await api.changePassword({
          previousPassword,
          newPassword,
        });
        setNotice(res.message ?? "Password updated.");
        currentIn.value = "";
        newIn.value = "";
        confirmIn.value = "";
      } catch (err) {
        setError(errMessage(err));
      } finally {
        setBusy(null);
        render();
      }
    })();
  });

  panel.append(form);
  return panel;
}

function profileDeletePanel(): HTMLElement {
  const user = state.user!;
  const panel = el("section", { className: "panel" });
  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Delete my account"),
        el(
          "p",
          { className: "sub" },
          "Deletes this account, connected mailboxes, stored credentials, and personal access tokens. Gmail grants are revoked with Google. We keep the account email and a usage log of tool names and times, not message contents, for 18 months so a card payment can be defended. This cannot be undone.",
        ),
      ),
    ),
  );

  const form = el("form", { className: "profile-form" }) as HTMLFormElement;
  const emailIn = el("input", {
    type: "email",
    className: "input",
    name: "confirmEmail",
    autocomplete: "off",
    placeholder: user.email,
    value: "",
  }) as HTMLInputElement;
  form.append(
    el(
      "label",
      { className: "field" },
      "Type your account email to confirm",
      emailIn,
    ),
  );
  const actions = el("div", { className: "row-actions" });
  const deleteBtn = el(
    "button",
    {
      type: "submit",
      className: "btn-danger btn-sm",
      disabled: state.busy === "delete-me" ? true : undefined,
    },
    state.busy === "delete-me" ? "Deleting…" : "Delete my account",
  ) as HTMLButtonElement;
  actions.append(deleteBtn);
  form.append(actions);
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const typed = emailIn.value.trim();
    if (typed.toLowerCase() !== user.email.trim().toLowerCase()) {
      setError("Type your account email to confirm.");
      render();
      return;
    }
    if (
      !confirm(
        `Delete ${user.email} and every mailbox connected to it? Message contents are deleted. The account email and a usage log are kept for 18 months for payment disputes. This cannot be undone.`,
      )
    ) {
      return;
    }
    void onDeleteOwnAccount(typed);
  });
  panel.append(form);
  return panel;
}

/** Admin-only dashboard at /admin. */
function renderAdminShell(): HTMLElement {
  const shell = el("div", { className: "app-shell" });
  const isAdmin = state.user && isAdminRole(state.user.role);

  shell.append(renderTopbar({ adminPage: true }));

  if (!isAdmin) {
    shell.append(
      alertBox(
        "error",
        "You need an owner or admin role to open this page.",
      ),
      el(
        "p",
        { className: "hint" },
        el("a", { href: "/" }, "Back to dashboard"),
      ),
    );
    shell.querySelector('a[href="/"]')?.addEventListener("click", (ev) => {
      ev.preventDefault();
      navigateTo("/");
    });
    return shell;
  }

  if (state.error) shell.append(alertBox("error", state.error));
  if (state.notice) shell.append(alertBox("ok", state.notice));

  shell.append(adminJoinRequestsPanel());
  shell.append(adminInvitesPanel());
  shell.append(adminUsersPanel());
  shell.append(adminFlagsPanel());
  return shell;
}

/** Modal dialog shell (centered overlay). */
function modalDialog(opts: {
  title: string;
  body: HTMLElement;
  onClose: () => void;
}): HTMLElement {
  const overlay = el("div", {
    className: "modal-overlay",
    role: "presentation",
  });
  const dialog = el("div", {
    className: "modal-dialog",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": opts.title,
  });
  const head = el("div", { className: "modal-header" });
  head.append(
    el("h2", { className: "modal-title" }, opts.title),
    el(
      "button",
      {
        type: "button",
        className: "btn-ghost btn-sm modal-close",
        "aria-label": "Close",
      },
      "✕",
    ),
  );
  head.querySelector("button")?.addEventListener("click", () => {
    opts.onClose();
  });
  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay) opts.onClose();
  });
  dialog.append(head, el("div", { className: "modal-body" }, opts.body));
  overlay.append(dialog);
  // Escape to close
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === "Escape") {
      opts.onClose();
      document.removeEventListener("keydown", onKey);
    }
  };
  document.addEventListener("keydown", onKey);
  return overlay;
}

function closeAccountDialog(): void {
  state.accountDialog = null;
  state.imapDraft = null;
  render();
}

function accountConnectDialog(kind: "gmail" | "imap"): HTMLElement {
  if (kind === "gmail") {
    return modalDialog({
      title: "Connect Gmail",
      body: gmailConnectDialogBody(),
      onClose: closeAccountDialog,
    });
  }
  return modalDialog({
    title: "Connect IMAP",
    body: imapForm({ inDialog: true }),
    onClose: closeAccountDialog,
  });
}

function pendingInvitePanel(): HTMLElement {
  const panel = el("section", { className: "panel invite-pending-panel" });
  panel.append(
    el("h2", null, "Pending invite"),
    el(
      "p",
      { className: "sub" },
      state.invite.preview
        ? `Invite for ${state.invite.preview.email} is ready to accept for this session.`
        : "You have an invite token stored for this browser tab.",
    ),
  );
  if (state.invite.preview) {
    panel.append(inviteBanner());
  }
  const actions = el("div", { className: "form-actions" });
  const acceptBtn = el(
    "button",
    {
      type: "button",
      className: "btn-primary",
      disabled:
        state.busy === "invite-accept" || !state.invite.token
          ? true
          : undefined,
    },
    state.busy === "invite-accept" ? "Accepting…" : "Accept invite",
  ) as HTMLButtonElement;
  acceptBtn.addEventListener("click", () => {
    void (async () => {
      const ok = await tryAcceptPendingInvite();
      if (ok) {
        try {
          await refreshDashboard();
        } catch (err) {
          setError(errMessage(err));
        }
      }
      render();
    })();
  });
  const dismissBtn = el(
    "button",
    { type: "button", className: "btn-ghost" },
    "Dismiss",
  );
  dismissBtn.addEventListener("click", () => {
    clearSessionInviteToken();
    state.invite.token = null;
    state.invite.preview = null;
    render();
  });
  actions.append(acceptBtn, dismissBtn);
  panel.append(actions);
  return panel;
}

function alertBox(kind: "error" | "warn" | "ok" | "info", msg: string): HTMLElement {
  return el("div", { className: `alert alert-${kind}`, role: "alert" }, msg);
}

// ── Accounts ──────────────────────────────────────────────────────────────

/** Human-readable account status for badges (API may send needs_reauth). */
export function formatAccountStatus(status: string): string {
  const s = status.toLowerCase();
  if (s === "needs_reauth") return "Needs re-auth";
  if (s === "ok") return "OK";
  if (s === "error") return "Error";
  if (s === "pending") return "Pending";
  if (s === "active") return "Active";
  return status;
}

/** Days since lastVerifiedAt (null if missing/invalid). */
export function daysSinceVerified(lastVerifiedAt?: string): number | null {
  if (!lastVerifiedAt) return null;
  const t = Date.parse(lastVerifiedAt);
  if (!Number.isFinite(t)) return null;
  return (Date.now() - t) / (1000 * 60 * 60 * 24);
}

/**
 * Gmail row should offer Reconnect when status is needs_reauth/error,
 * or when last verified is older than ~6 days (Testing ~7-day tokens).
 */
export function gmailNeedsReconnect(a: Account): boolean {
  if (a.providerKind !== "gmail") return false;
  const s = a.status.toLowerCase();
  if (s === "needs_reauth" || s === "error") return true;
  const days = daysSinceVerified(a.lastVerifiedAt);
  return days != null && days > 6;
}

function accountsPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Mail accounts"),
        el(
          "p",
          { className: "sub" },
          "Connected mailboxes. Click a row to edit. Use Connect Gmail (OAuth) or Connect IMAP to add one.",
        ),
      ),
      el(
        "div",
        { className: "account-connect-actions" },
        el(
          "button",
          {
            type: "button",
            className: "btn-primary btn-sm",
            "data-action": "connect-gmail",
          },
          "Connect Gmail",
        ),
        el(
          "button",
          {
            type: "button",
            className: "btn-secondary btn-sm",
            "data-action": "connect-imap",
          },
          "Connect IMAP",
        ),
      ),
    ),
  );

  panel
    .querySelector('[data-action="connect-gmail"]')
    ?.addEventListener("click", () => {
      state.accountDialog = "gmail";
      setError(null);
      render();
    });
  panel
    .querySelector('[data-action="connect-imap"]')
    ?.addEventListener("click", () => {
      state.accountDialog = "imap";
      state.imapDraft = emptyImapDraft();
      setError(null);
      render();
    });

  const reauthAccounts = state.accounts.filter(
    (a) => a.providerKind === "gmail" && a.status.toLowerCase() === "needs_reauth",
  );
  const agingOk = state.accounts.filter(
    (a) =>
      a.providerKind === "gmail" &&
      a.status.toLowerCase() === "ok" &&
      gmailNeedsReconnect(a),
  );
  const scopeDeficit = state.accounts.filter((a) => {
    if (a.providerKind !== "gmail") return false;
    return googleServicesNeedConsent(
      prefsFromAccount(a),
      a.googleGrantedScopes,
    );
  });

  if (reauthAccounts.length > 0) {
    const names = reauthAccounts.map((a) => a.address).join(", ");
    panel.append(
      alertBox(
        "warn",
        `Needs re-auth: ${names}. Use Reconnect Gmail on each account.`,
      ),
    );
  } else if (agingOk.length > 0) {
    const names = agingOk.map((a) => a.address).join(", ");
    panel.append(
      alertBox(
        "warn",
        `Gmail connection may expire soon (${names}). Reconnect before mail tools fail.`,
      ),
    );
  }
  if (scopeDeficit.length > 0) {
    const names = scopeDeficit.map((a) => a.address).join(", ");
    panel.append(
      alertBox(
        "warn",
        `Google services need consent: ${names}. Open the account and click Apply with Google to grant the missing scopes.`,
      ),
    );
  }

  // Account list — edit panel sits inline under the selected row
  const list = el("div", { className: "list" });
  if (state.accounts.length === 0) {
    list.append(
      el(
        "p",
        { className: "empty" },
        "No accounts yet. Connect Gmail or IMAP to get started.",
      ),
    );
  } else {
    list.append(
      el(
        "p",
        { className: "hint", style: "margin-bottom:0.5rem" },
        "Click an account to edit settings, credentials, or Google services.",
      ),
    );
    for (const a of state.accounts) {
      list.append(accountRow(a));
      if (state.selectedAccountId === a.id) {
        list.append(accountEditPanel(a));
      }
    }
  }
  panel.append(list);

  return panel;
}

/**
 * Gmail connect dialog body: optional Google services, then continue to OAuth.
 */
const GOOGLE_SERVICE_OPTIONS: Array<{
  key: keyof GoogleServicesPrefs;
  label: string;
  help: string;
  defaultOn: boolean;
}> = [
  {
    key: "contacts",
    label: "Contacts + directory",
    help: "Saved contacts, Other contacts, and Workspace directory lookup",
    defaultOn: true,
  },
  {
    key: "calendar",
    label: "Calendar",
    help: "Events, calendar list, free/busy",
    defaultOn: true,
  },
  {
    key: "settings",
    label: "Settings / account",
    help: "Send-as aliases, vacation responder, profile settings",
    defaultOn: true,
  },
  {
    key: "tasks",
    label: "Tasks",
    help: "Google Tasks lists and to-dos",
    defaultOn: true,
  },
  {
    key: "drive",
    label: "Drive",
    help: "My Drive + Shared Drives (Team Drives) — search, download, upload, share",
    defaultOn: true,
  },
  {
    key: "docs",
    label: "Docs",
    help: "Create and edit Google Docs (pairs with Drive)",
    defaultOn: true,
  },
  {
    key: "sheets",
    label: "Sheets",
    help: "Create and edit Google Sheets cells",
    defaultOn: true,
  },
  {
    key: "slides",
    label: "Slides",
    help: "Create Google Slides and replace text",
    defaultOn: true,
  },
  {
    key: "meet",
    label: "Meet",
    help: "Create Meet spaces (or use add_meet on calendar events without this)",
    defaultOn: true,
  },
];

function prefsFromAccount(a: Account): GoogleServicesPrefs {
  return {
    contacts: a.googleServices?.contacts === true,
    calendar: a.googleServices?.calendar === true,
    settings: a.googleServices?.settings === true,
    drive: a.googleServices?.drive === true,
    docs: a.googleServices?.docs === true,
    sheets: a.googleServices?.sheets === true,
    slides: a.googleServices?.slides === true,
    tasks: a.googleServices?.tasks === true,
    meet: a.googleServices?.meet === true,
  };
}

function gmailConnectDialogBody(): HTMLElement {
  const box = el("div", { className: "form-stack" });
  const boxes = new Map<keyof GoogleServicesPrefs, HTMLInputElement>();
  for (const opt of GOOGLE_SERVICE_OPTIONS) {
    boxes.set(opt.key, scopeCheckboxLike(`connect-${opt.key}`, opt.defaultOn));
  }

  const services = el("fieldset", { className: "scope-fieldset" });
  services.append(
    el("legend", null, "Google services"),
    el(
      "p",
      { className: "hint" },
      "Mail (read/send/labels) is always included. Choose extras for the Google consent screen — you can change them later on the account.",
    ),
  );
  for (const opt of GOOGLE_SERVICE_OPTIONS) {
    const cb = boxes.get(opt.key)!;
    const row = el("label", { className: "scope-option" });
    row.append(cb, el("span", { className: "scope-label" }, opt.label));
    services.append(
      el(
        "div",
        { className: "scope-row" },
        row,
        el("span", { className: "scope-help" }, opt.help),
      ),
    );
  }

  const actions = el("div", { className: "form-actions" });
  const cancelBtn = el(
    "button",
    { type: "button", className: "btn-ghost" },
    "Cancel",
  );
  cancelBtn.addEventListener("click", () => {
    closeAccountDialog();
  });
  const gmailBtn = el(
    "button",
    {
      type: "button",
      className: "btn-primary",
      disabled: state.busy === "gmail-start" ? true : undefined,
    },
    state.busy === "gmail-start" ? "Redirecting…" : "Continue with Google",
  ) as HTMLButtonElement;
  gmailBtn.addEventListener("click", () => {
    const selected: GoogleServicesPrefs = {
      contacts: false,
      calendar: false,
      settings: false,
    };
    for (const opt of GOOGLE_SERVICE_OPTIONS) {
      selected[opt.key] = boxes.get(opt.key)!.checked;
    }
    void onConnectGmail(undefined, selected);
  });
  actions.append(cancelBtn, gmailBtn);
  box.append(services, actions);
  return box;
}

function statusBadge(status: string): HTMLElement {
  const s = status.toLowerCase();
  let cls = "badge-muted";
  if (s === "ok" || s === "active") cls = "badge-ok";
  else if (s === "needs_reauth") cls = "badge-err";
  else if (s === "error") cls = "badge-err";
  else if (s === "pending") cls = "badge-warn";
  return el("span", { className: `badge ${cls}` }, formatAccountStatus(status));
}

function accountRow(a: Account): HTMLElement {
  const selected = state.selectedAccountId === a.id;
  const row = el("div", {
    className: selected ? "list-item list-item-selected" : "list-item",
    role: "button",
    tabindex: "0",
    title: "Click to edit account",
  });
  const title = el("div", { className: "list-item-title" }, a.address);
  title.append(statusBadge(a.status));
  if (a.preset) {
    title.append(el("span", { className: "badge badge-muted" }, a.preset));
  }
  if (a.providerKind === "gmail") {
    title.append(el("span", { className: "badge badge-muted" }, "gmail"));
    const gs = a.googleServices;
    if (gs?.contacts) title.append(el("span", { className: "badge badge-muted" }, "contacts"));
    if (gs?.calendar) title.append(el("span", { className: "badge badge-muted" }, "calendar"));
    if (gs?.settings) title.append(el("span", { className: "badge badge-muted" }, "settings"));
    if (gs?.drive) title.append(el("span", { className: "badge badge-muted" }, "drive"));
    if (gs?.docs) title.append(el("span", { className: "badge badge-muted" }, "docs"));
    if (gs?.sheets) title.append(el("span", { className: "badge badge-muted" }, "sheets"));
    if (gs?.slides) title.append(el("span", { className: "badge badge-muted" }, "slides"));
    if (gs?.tasks) title.append(el("span", { className: "badge badge-muted" }, "tasks"));
    if (gs?.meet) title.append(el("span", { className: "badge badge-muted" }, "meet"));
  }

  const days = daysSinceVerified(a.lastVerifiedAt);
  const agingHint =
    a.providerKind === "gmail" &&
    a.status.toLowerCase() === "ok" &&
    days != null &&
    days > 6
      ? `~${Math.floor(days)}d since verify — reconnect soon`
      : null;

  const maxMb =
    typeof a.maxMessageBytes === "number" && a.maxMessageBytes > 0
      ? `max attach ~${formatBytes(a.maxMessageBytes)}`
      : null;
  const metaParts = [
    a.providerKind,
    a.displayName,
    maxMb,
    a.lastVerifiedAt ? `verified ${formatDate(a.lastVerifiedAt)}` : null,
    agingHint,
    a.lastError ? `error: ${a.lastError}` : null,
    selected ? "editing" : "click to edit",
  ].filter(Boolean);

  const actions = el("div", { className: "list-item-actions" });

  if (gmailNeedsReconnect(a)) {
    const isHard =
      a.status.toLowerCase() === "needs_reauth" ||
      a.status.toLowerCase() === "error";
    const reconnectBtn = el(
      "button",
      {
        type: "button",
        className: isHard ? "btn-primary btn-sm" : "btn-secondary btn-sm",
        disabled: state.busy === `gmail-re-${a.id}` ? true : undefined,
        title: isHard
          ? "Refresh Google OAuth tokens for this Gmail account"
          : "Proactive reconnect — Testing tokens expire ~every 7 days",
      },
      state.busy === `gmail-re-${a.id}` ? "…" : "Reconnect Gmail",
    );
    reconnectBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      void onConnectGmail(a.id);
    });
    actions.append(reconnectBtn);
  }

  row.append(
    el(
      "div",
      { className: "list-item-main" },
      title,
      el("div", { className: "list-item-meta" }, metaParts.join(" · ")),
    ),
    actions,
  );

  const select = (): void => {
    state.selectedAccountId =
      state.selectedAccountId === a.id ? null : a.id;
    setError(null);
    render();
  };
  row.addEventListener("click", select);
  row.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      select();
    }
  });
  return row;
}

/** Edit form for a selected account (IMAP or Gmail). */
function accountEditPanel(a: Account): HTMLElement {
  const box = el("div", {
    className: "account-edit",
    role: "region",
    "aria-label": `Edit ${a.address}`,
  });
  box.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h3", { className: "panel-h3" }, `Edit ${a.address}`),
        el(
          "p",
          { className: "hint" },
          a.providerKind === "gmail"
            ? "Update display name and Google services. Enabling new services requires Google consent — Apply with Google appears when needed."
            : "Update IMAP connection details. Leave app password blank to keep the current one.",
        ),
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost btn-sm" },
        "Close",
      ),
    ),
  );
  const closeBtn = box.querySelector("button");
  closeBtn?.addEventListener("click", () => {
    state.selectedAccountId = null;
    render();
  });

  if (a.providerKind === "gmail") {
    box.append(gmailEditForm(a));
  } else {
    box.append(imapEditForm(a));
  }
  return box;
}

/** True when preferred services need OAuth scopes not yet granted. */
function grantedHasAuthScope(granted: string | undefined, scopeUrl: string): boolean {
  if (!granted) return false;
  const want = scopeUrl.toLowerCase();
  const wantPath = want.includes("/auth/") ? want.slice(want.indexOf("/auth/")) : want;
  for (const part of granted.toLowerCase().split(/\s+/).filter(Boolean)) {
    if (part === want || part === wantPath || part.endsWith(wantPath)) return true;
  }
  return false;
}

function googleServicesNeedConsent(
  services: GoogleServicesPrefs,
  grantedScopes: string | undefined,
): boolean {
  const g = grantedScopes ?? "";
  if (services.contacts && !grantedHasAuthScope(g, "https://www.googleapis.com/auth/contacts")) {
    return true;
  }
  if (services.calendar && !grantedHasAuthScope(g, "https://www.googleapis.com/auth/calendar.events")) {
    return true;
  }
  if (services.settings) {
    if (
      !grantedHasAuthScope(g, "https://www.googleapis.com/auth/gmail.settings.basic") ||
      !grantedHasAuthScope(g, "https://www.googleapis.com/auth/gmail.settings.sharing")
    ) {
      return true;
    }
  }
  if (services.drive && !grantedHasAuthScope(g, "https://www.googleapis.com/auth/drive")) {
    return true;
  }
  if (services.docs) {
    if (
      !grantedHasAuthScope(g, "https://www.googleapis.com/auth/drive") ||
      !grantedHasAuthScope(g, "https://www.googleapis.com/auth/documents")
    ) {
      return true;
    }
  }
  if (services.sheets) {
    if (
      !grantedHasAuthScope(g, "https://www.googleapis.com/auth/drive") ||
      !grantedHasAuthScope(g, "https://www.googleapis.com/auth/spreadsheets")
    ) {
      return true;
    }
  }
  if (services.slides) {
    if (
      !grantedHasAuthScope(g, "https://www.googleapis.com/auth/drive") ||
      !grantedHasAuthScope(g, "https://www.googleapis.com/auth/presentations")
    ) {
      return true;
    }
  }
  if (services.tasks && !grantedHasAuthScope(g, "https://www.googleapis.com/auth/tasks")) {
    return true;
  }
  if (
    services.meet &&
    !grantedHasAuthScope(g, "https://www.googleapis.com/auth/meetings.space.created") &&
    !grantedHasAuthScope(g, "https://www.googleapis.com/auth/meetings.space.readonly")
  ) {
    return true;
  }
  return false;
}

function gmailEditForm(a: Account): HTMLElement {
  const form = el("form", { className: "form-stack" });
  const displayName = el("input", {
    type: "text",
    name: "displayName",
    value: a.displayName ?? "",
    placeholder: "From display name",
    autocomplete: "off",
  }) as HTMLInputElement;

  const baseline = prefsFromAccount(a);
  const boxes = new Map<keyof GoogleServicesPrefs, HTMLInputElement>();
  for (const opt of GOOGLE_SERVICE_OPTIONS) {
    boxes.set(opt.key, scopeCheckboxLike(opt.key, baseline[opt.key] === true));
  }

  const services = el("fieldset", { className: "scope-fieldset" });
  services.append(
    el("legend", null, "Google services"),
    el(
      "p",
      { className: "hint" },
      "Mail is always on. Toggle extras and Save. Apply with Google appears when you enable services that need new OAuth scopes (or when scopes are still missing). Enable Drive, Docs, Sheets, Slides, Tasks, and Meet APIs in GCP before using those tools.",
    ),
  );
  for (const opt of GOOGLE_SERVICE_OPTIONS) {
    const cb = boxes.get(opt.key)!;
    const row = el("label", { className: "scope-option" });
    row.append(cb, el("span", { className: "scope-label" }, opt.label));
    services.append(
      el("div", { className: "scope-row" }, row, el("span", { className: "scope-help" }, opt.help)),
    );
  }

  const actions = el("div", { className: "form-actions" });
  const saveBtn = el(
    "button",
    {
      type: "submit",
      className: "btn-primary",
      disabled: state.busy === `edit-${a.id}` ? true : undefined,
    },
    state.busy === `edit-${a.id}` ? "Saving…" : "Save",
  );
  const applyBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary",
      disabled: state.busy === `gmail-re-${a.id}` ? true : undefined,
      title: "Reconnect Google with the selected service scopes",
      hidden: true,
    },
    state.busy === `gmail-re-${a.id}` ? "Redirecting…" : "Apply with Google",
  ) as HTMLButtonElement;
  const delBtn = el(
    "button",
    {
      type: "button",
      className: "btn-danger",
      disabled: state.busy === `del-acc-${a.id}` ? true : undefined,
    },
    state.busy === `del-acc-${a.id}` ? "…" : "Disconnect",
  ) as HTMLButtonElement;

  const readServices = (): GoogleServicesPrefs => {
    const out: GoogleServicesPrefs = {
      contacts: false,
      calendar: false,
      settings: false,
    };
    for (const opt of GOOGLE_SERVICE_OPTIONS) {
      out[opt.key] = boxes.get(opt.key)!.checked;
    }
    return out;
  };

  /** Show Apply only when selected services need Google OAuth scopes not yet granted. */
  const syncApplyVisibility = (): void => {
    // Display-name-only (or disable-only) edits never need Apply — Save is enough.
    // Enabling services that lack granted scopes (or opening with a scope deficit) shows it.
    applyBtn.hidden = !googleServicesNeedConsent(
      readServices(),
      a.googleGrantedScopes,
    );
  };
  for (const cb of boxes.values()) {
    cb.addEventListener("change", syncApplyVisibility);
  }
  syncApplyVisibility();

  form.append(
    el("label", { className: "field" }, "Display name", displayName),
    services,
    actions,
  );
  actions.append(saveBtn, applyBtn, delBtn);

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onUpdateAccount(a.id, {
      displayName: displayName.value.trim() || null,
      googleServices: readServices(),
    });
  });
  applyBtn.addEventListener("click", () => {
    const servicesBody = readServices();
    void (async () => {
      // Persist prefs first so reconnect uses them if body omits services.
      try {
        await api.updateAccount(a.id, {
          displayName: displayName.value.trim() || null,
          googleServices: servicesBody,
        });
      } catch {
        // still try OAuth
      }
      void onConnectGmail(a.id, servicesBody);
    })();
  });
  delBtn.addEventListener("click", () => {
    if (
      !confirm(
        `Disconnect ${a.address}? This removes the mailbox, deletes the stored credentials, and asks Google to revoke the grant.`,
      )
    ) {
      return;
    }
    void onDeleteAccount(a.id);
  });

  if (a.googleGrantedScopes) {
    form.append(
      el(
        "p",
        { className: "hint" },
        "Granted scopes: ",
        el("code", { className: "inline-code" }, a.googleGrantedScopes.slice(0, 120) + (a.googleGrantedScopes.length > 120 ? "…" : "")),
      ),
    );
  }
  return form;
}

function imapEditForm(a: Account): HTMLElement {
  const form = el("form", { className: "form-stack" });
  const displayName = el("input", {
    type: "text",
    name: "displayName",
    value: a.displayName ?? "",
    placeholder: "Optional label",
    autocomplete: "off",
  }) as HTMLInputElement;
  const appPassword = el("input", {
    type: "password",
    name: "appPassword",
    placeholder: "Leave blank to keep current",
    autocomplete: "new-password",
  }) as HTMLInputElement;
  const imapHost = el("input", {
    type: "text",
    name: "imapHost",
    value: a.imapHost ?? "",
    placeholder: "imap.example.com",
    autocomplete: "off",
  }) as HTMLInputElement;
  const smtpHost = el("input", {
    type: "text",
    name: "smtpHost",
    value: a.smtpHost ?? "",
    placeholder: "smtp.example.com",
    autocomplete: "off",
  }) as HTMLInputElement;
  const imapPort = el("input", {
    type: "number",
    name: "imapPort",
    value: a.imapPort != null ? String(a.imapPort) : "",
    min: "1",
    max: "65535",
  }) as HTMLInputElement;
  const smtpPort = el("input", {
    type: "number",
    name: "smtpPort",
    value: a.smtpPort != null ? String(a.smtpPort) : "",
    min: "1",
    max: "65535",
  }) as HTMLInputElement;
  const maxMb = el("input", {
    type: "number",
    name: "maxMessageMb",
    value:
      a.maxMessageBytes != null
        ? String(Math.round(a.maxMessageBytes / (1024 * 1024)))
        : "",
    min: "1",
    max: "25",
    step: "1",
  }) as HTMLInputElement;
  const smtpSecure = el("input", {
    type: "checkbox",
    name: "smtpSecure",
    checked: a.smtpSecure === true ? true : undefined,
  }) as HTMLInputElement;

  form.append(
    el(
      "p",
      { className: "hint" },
      `Preset: ${a.preset ?? "—"} · Address cannot be changed (delete and re-add).`,
    ),
    el("label", { className: "field" }, "Display name", displayName),
    el(
      "label",
      { className: "field" },
      "App password",
      withShowPassword(appPassword),
    ),
    el(
      "div",
      { className: "form-row" },
      el("label", { className: "field" }, "IMAP host", imapHost),
      el("label", { className: "field" }, "IMAP port", imapPort),
    ),
    el(
      "div",
      { className: "form-row" },
      el("label", { className: "field" }, "SMTP host", smtpHost),
      el("label", { className: "field" }, "SMTP port", smtpPort),
    ),
    el(
      "label",
      { className: "scope-option" },
      smtpSecure,
      el("span", { className: "scope-label" }, "SMTP implicit TLS (port 465)"),
    ),
    el("label", { className: "field" }, "Max message size (MB)", maxMb),
  );

  const actions = el("div", { className: "form-actions" });
  const saveBtn = el(
    "button",
    {
      type: "submit",
      className: "btn-primary",
      disabled: state.busy === `edit-${a.id}` ? true : undefined,
    },
    state.busy === `edit-${a.id}` ? "Saving…" : "Save",
  );
  const delBtn = el(
    "button",
    {
      type: "button",
      className: "btn-danger",
      disabled: state.busy === `del-acc-${a.id}` ? true : undefined,
    },
    state.busy === `del-acc-${a.id}` ? "…" : "Disconnect",
  ) as HTMLButtonElement;
  actions.append(saveBtn, delBtn);
  form.append(actions);

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const body: api.UpdateAccountBody = {
      displayName: displayName.value.trim() || null,
    };
    const ih = imapHost.value.trim();
    const sh = smtpHost.value.trim();
    body.imapHost = ih || null;
    body.smtpHost = sh || null;
    const ip = Number(imapPort.value);
    const sp = Number(smtpPort.value);
    body.imapPort = imapPort.value && Number.isFinite(ip) ? ip : null;
    body.smtpPort = smtpPort.value && Number.isFinite(sp) ? sp : null;
    body.smtpSecure = smtpSecure.checked;
    const mm = Number(maxMb.value);
    if (maxMb.value && Number.isFinite(mm) && mm > 0) {
      body.maxMessageBytes = Math.round(mm * 1024 * 1024);
    } else if (maxMb.value === "") {
      body.maxMessageBytes = null;
    }
    const pw = appPassword.value.replace(/\s+/g, "");
    if (pw) body.appPassword = pw;
    void onUpdateAccount(a.id, body);
  });
  delBtn.addEventListener("click", () => {
    if (
      !confirm(
        `Disconnect ${a.address}? This removes the mailbox and deletes the stored password.`,
      )
    ) {
      return;
    }
    void onDeleteAccount(a.id);
  });
  return form;
}

function scopeCheckboxLike(name: string, checked: boolean): HTMLInputElement {
  return el("input", {
    type: "checkbox",
    name,
    checked: checked ? true : undefined,
  }) as HTMLInputElement;
}

function currentImapDraft(): ImapDraft {
  if (!state.imapDraft) state.imapDraft = emptyImapDraft();
  return state.imapDraft;
}

function rememberImapField(
  input: HTMLInputElement,
  key: "address" | "appPassword" | "displayName" | "imapHost" | "imapPort" | "smtpHost" | "smtpPort" | "maxMessageMb",
): void {
  input.addEventListener("input", () => {
    const draft = currentImapDraft();
    draft[key] = input.value;
    if (
      key === "imapHost" ||
      key === "imapPort" ||
      key === "smtpHost" ||
      key === "smtpPort"
    ) {
      draft.hostsOverridden = true;
      if (key === "smtpPort" && input.value === "465") draft.smtpSecure = true;
      if (key === "smtpPort" && input.value === "587") draft.smtpSecure = false;
    }
  });
}

function applyDiscoveryHosts(draft: ImapDraft, found: api.ImapDiscovery): void {
  if (draft.hostsOverridden) return;
  if (found.found && found.imapHost && found.smtpHost) {
    draft.imapHost = found.imapHost;
    draft.imapPort = found.imapPort != null ? String(found.imapPort) : "";
    draft.smtpHost = found.smtpHost;
    draft.smtpPort = found.smtpPort != null ? String(found.smtpPort) : "";
    draft.smtpSecure = typeof found.smtpSecure === "boolean" ? found.smtpSecure : null;
    return;
  }
  draft.imapHost = "";
  draft.imapPort = "";
  draft.smtpHost = "";
  draft.smtpPort = "";
  draft.smtpSecure = null;
}

async function lookupImapServers(address: string): Promise<void> {
  const seq = ++state.imapDiscoverSeq;
  const draft = currentImapDraft();
  draft.address = address;
  draft.preset = null;
  if (!address.includes("@") || address.endsWith("@")) {
    draft.discoveryNote = null;
    draft.instructions = null;
    paintImapDiscovery(draft);
    return;
  }
  draft.discoveryNote = "Looking up mail servers…";
  draft.instructions = null;
  paintImapDiscovery(draft);
  try {
    const found = await api.discoverImap(address);
    if (seq !== state.imapDiscoverSeq) return;
    const current = state.imapDraft;
    if (!current || current.address.trim() !== address) return;
    current.passwordLabel = found.passwordLabel || "Mailbox password";
    current.instructions = found.instructions || found.note || null;
    current.discoveryNote = found.summary || null;
    current.preset = found.preset ?? null;
    applyDiscoveryHosts(current, found);
    paintImapDiscovery(current);
  } catch (err) {
    if (seq !== state.imapDiscoverSeq) return;
    const current = state.imapDraft;
    if (!current || current.address.trim() !== address) return;
    current.discoveryNote = errMessage(err);
    current.instructions = null;
    paintImapDiscovery(current);
  }
}

function discoveryStatusText(draft: ImapDraft): string {
  if (!draft.discoveryNote) return "";
  const override =
    draft.hostsOverridden && !draft.discoveryNote.startsWith("Looking up")
      ? " Server settings override this."
      : "";
  return `${draft.discoveryNote}${override}`;
}

/** Update the open dialog without rebuilding it, so typed fields keep focus. */
function paintImapDiscovery(draft: ImapDraft): void {
  const status = document.querySelector("[data-discovery-status]");
  if (status instanceof HTMLElement) {
    const text = discoveryStatusText(draft);
    status.textContent = text;
    status.hidden = !text;
  }
  const instructions = document.querySelector("[data-setup-instructions]");
  if (instructions instanceof HTMLElement) {
    instructions.textContent = draft.instructions ?? "";
    instructions.hidden = !draft.instructions;
  }
  const passwordLabel = document.querySelector("[data-password-label]");
  if (passwordLabel instanceof HTMLElement) {
    passwordLabel.textContent = draft.passwordLabel;
  }
  if (draft.hostsOverridden) return;
  const set = (name: string, value: string) => {
    const input = document.querySelector(
      `input[name="${name}"]`,
    ) as HTMLInputElement | null;
    if (input) input.value = value;
  };
  set("imapHost", draft.imapHost);
  set("imapPort", draft.imapPort);
  set("smtpHost", draft.smtpHost);
  set("smtpPort", draft.smtpPort);
}

function imapForm(opts?: { inDialog?: boolean }): HTMLElement {
  const draft = currentImapDraft();
  const form = el("form", { className: "form-stack", novalidate: true });
  const address = el("input", {
    type: "email",
    name: "address",
    required: true,
    placeholder: "you@example.com",
    autocomplete: "off",
    value: draft.address,
  }) as HTMLInputElement;
  const appPassword = el("input", {
    type: "password",
    name: "appPassword",
    required: true,
    placeholder: "Mailbox or app password",
    autocomplete: "new-password",
    value: draft.appPassword,
  }) as HTMLInputElement;
  const displayName = el("input", {
    type: "text",
    name: "displayName",
    placeholder: "Optional label",
    autocomplete: "off",
    value: draft.displayName,
  }) as HTMLInputElement;

  const imapHost = el("input", {
    type: "text",
    name: "imapHost",
    placeholder: "imap.example.com",
    autocomplete: "off",
    value: draft.imapHost,
  }) as HTMLInputElement;
  const smtpHost = el("input", {
    type: "text",
    name: "smtpHost",
    placeholder: "smtp.example.com",
    autocomplete: "off",
    value: draft.smtpHost,
  }) as HTMLInputElement;
  const imapPort = el("input", {
    type: "number",
    name: "imapPort",
    placeholder: "993",
    min: "1",
    max: "65535",
    value: draft.imapPort,
  }) as HTMLInputElement;
  const smtpPort = el("input", {
    type: "number",
    name: "smtpPort",
    placeholder: "465",
    min: "1",
    max: "65535",
    value: draft.smtpPort,
  }) as HTMLInputElement;
  const maxMessageMb = el("input", {
    type: "number",
    name: "maxMessageMb",
    placeholder: "20",
    min: "1",
    max: "25",
    step: "1",
    value: draft.maxMessageMb,
  }) as HTMLInputElement;
  for (const [input, key] of [
    [address, "address"],
    [appPassword, "appPassword"],
    [displayName, "displayName"],
    [imapHost, "imapHost"],
    [imapPort, "imapPort"],
    [smtpHost, "smtpHost"],
    [smtpPort, "smtpPort"],
    [maxMessageMb, "maxMessageMb"],
  ] as const) {
    rememberImapField(input, key);
  }
  address.addEventListener("blur", () => {
    void lookupImapServers(address.value.trim());
  });

  const advanced = el("details", {
    className: "advanced",
    ...(draft.hostsOverridden ? { open: true } : {}),
  });
  advanced.append(
    el("summary", null, "Server settings"),
    el(
      "div",
      { className: "form-stack" },
      el(
        "p",
        { className: "hint" },
        "Override the lookup. Change these only when your provider gave you different servers.",
      ),
      el(
        "div",
        { className: "form-row" },
        el("label", { className: "field" }, "IMAP host", imapHost),
        el("label", { className: "field" }, "IMAP port", imapPort),
      ),
      el(
        "div",
        { className: "form-row" },
        el("label", { className: "field" }, "SMTP host", smtpHost),
        el("label", { className: "field" }, "SMTP port", smtpPort),
      ),
      el(
        "label",
        { className: "field" },
        "Max message size (MB)",
        maxMessageMb,
      ),
      el(
        "p",
        { className: "hint" },
        "Total email size including attachments. Defaults: Gmail 25, iCloud 20, Outlook/Yahoo/Fastmail 25, generic 20. Only needed for custom IMAP servers.",
      ),
    ),
  );

  const actions = el("div", { className: "form-actions" });
  if (opts?.inDialog) {
    const cancelBtn = el(
      "button",
      { type: "button", className: "btn-ghost" },
      "Cancel",
    );
    cancelBtn.addEventListener("click", () => {
      closeAccountDialog();
    });
    actions.append(cancelBtn);
  }
  actions.append(
    el(
      "button",
      {
        type: "submit",
        className: "btn-primary",
        disabled: state.busy === "add-imap" ? true : undefined,
      },
      state.busy === "add-imap" ? "Verifying…" : "Connect IMAP",
    ),
  );
  if (state.busy === "add-imap") {
    actions.append(
      el(
        "p",
        { className: "discovery-status" },
        "Checking the mailbox. Some providers take up to a minute to accept or reject the password.",
      ),
    );
  }

  const statusText = discoveryStatusText(draft);
  if (state.error) form.append(alertBox("error", state.error));
  form.append(
    el("label", { className: "field" }, "Email address", address),
    el(
      "p",
      {
        className: "discovery-status",
        "data-discovery-status": "1",
        hidden: statusText ? undefined : true,
      },
      statusText,
    ),
    el(
      "p",
      {
        className: "setup-instructions",
        "data-setup-instructions": "1",
        hidden: draft.instructions ? undefined : true,
      },
      draft.instructions ?? "",
    ),
    el(
      "label",
      { className: "field" },
      el("span", { "data-password-label": "1" }, draft.passwordLabel),
      withShowPassword(appPassword),
    ),
    el("label", { className: "field" }, "Display name", displayName),
    advanced,
    actions,
  );

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const addr = address.value.trim();
    // Client-side guard — server also rejects @gmail.com / @googlemail.com.
    const domain = addr.split("@")[1]?.toLowerCase() ?? "";
    if (domain === "gmail.com" || domain === "googlemail.com") {
      setError(
        "Gmail addresses must use Connect Gmail (OAuth), not IMAP app passwords.",
      );
      render();
      return;
    }
    const body: api.CreateImapBody = {
      address: addr,
      appPassword: appPassword.value,
    };
    const dn = displayName.value.trim();
    if (dn) body.displayName = dn;
    const ih = imapHost.value.trim();
    const sh = smtpHost.value.trim();
    const kept = currentImapDraft();
    kept.address = addr;
    kept.appPassword = appPassword.value;
    kept.displayName = displayName.value;
    kept.imapHost = ih;
    kept.smtpHost = sh;
    kept.imapPort = imapPort.value;
    kept.smtpPort = smtpPort.value;
    kept.maxMessageMb = maxMessageMb.value;
    // Filled-in hosts for iCloud and the other known presets are a preview.
    // Sending them would switch the account to generic and drop the provider's
    // folder map and login retry. Send hosts only for an override or a
    // discovered generic server.
    const sendHosts =
      kept.hostsOverridden ||
      (kept.preset === "generic" && ih !== "" && sh !== "");
    if (sendHosts) {
      if (ih) body.imapHost = ih;
      if (sh) body.smtpHost = sh;
      const ip = Number(imapPort.value);
      const sp = Number(smtpPort.value);
      if (imapPort.value && Number.isFinite(ip)) body.imapPort = ip;
      if (smtpPort.value && Number.isFinite(sp)) body.smtpPort = sp;
      const secure =
        smtpPort.value === "465"
          ? true
          : smtpPort.value === "587"
            ? false
            : kept.smtpSecure;
      if (secure != null) body.smtpSecure = secure;
      if (ih && sh) body.preset = "generic";
    }
    const mm = Number(maxMessageMb.value);
    if (maxMessageMb.value && Number.isFinite(mm) && mm > 0) {
      body.maxMessageBytes = Math.round(mm * 1024 * 1024);
    }

    void onAddImap(body, () => {
      state.imapDraft = null;
    });
  });

  return form;
}

// ── MCP access (connect + active clients) ─────────────────────────────────

const SCOPE_HELP: Record<PatScope, string> = {
  read: "Discovery + read tools. Includes listing drafts and reading draft bodies.",
  send: "create/update/delete draft + send_message (implies read at enforce time).",
  full: "All tools including update/move/trash/create_mailbox.",
};

/**
 * Unified MCP panel: server URL front-and-center; setup steps collapsed.
 */
function mcpAccessPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  const cfg = mcpSetupConfig();
  const scopesEnforce = state.features["pats.scopesEnforce"] === true;

  panel.append(
    el("h2", null, "MCP access"),
    el(
      "p",
      { className: "sub" },
      "Paste this URL into Claude, Grok, ChatGPT, Cursor, Gemini, or another MCP host.",
    ),
  );

  // Only the server URL is always visible.
  const urlRow = el("div", { className: "mcp-url-row" });
  const urlCode = el("code", { className: "mcp-url-code" }, cfg.mcpResource);
  const copyBtn = el(
    "button",
    {
      type: "button",
      className: "btn-primary btn-sm",
      "data-copy": cfg.mcpResource,
    },
    "Copy",
  );
  copyBtn.addEventListener("click", async () => {
    const ok = await copyText(cfg.mcpResource);
    setNotice(ok ? "MCP URL copied" : "Could not copy");
    render();
  });
  urlRow.append(urlCode, copyBtn);
  panel.append(urlRow);

  const howTo = el("details", { className: "mcp-howto" });
  howTo.append(
    el("summary", null, "How to connect"),
    el(
      "div",
      { className: "mcp-howto-body" },
      el(
        "ol",
        { className: "connect-steps" },
        el(
          "li",
          null,
          "In Claude, Grok, ChatGPT, Cursor, Gemini, or another MCP host, add a ",
          el("strong", null, "custom connector"),
          " / remote MCP server.",
        ),
        el(
          "li",
          null,
          "Use the server URL above (copied). OAuth registration is automatic — no client id or secret to paste.",
        ),
        el(
          "li",
          null,
          "Complete sign-in when prompted",
          cfg.authHostedUiBase
            ? ` (login at ${cfg.authHostedUiBase.replace(/^https?:\/\//, "")}).`
            : " at the hosted login page.",
        ),
      ),
      state.features["docs.portal"]
        ? el(
            "p",
            { className: "hint" },
            el("a", { href: "/docs" }, "Config snippets and bridge docs → /docs"),
          )
        : null,
    ),
  );
  panel.append(howTo);

  panel.append(
    el("h3", { className: "panel-h3" }, "Active clients"),
    el(
      "p",
      { className: "hint" },
      "OAuth clients appear after they connect. Personal tokens appear when minted. Disconnect revokes access immediately.",
    ),
  );

  if (state.minted) {
    panel.append(mintedSecretBox(state.minted));
  }

  const list = el("div", { className: "list" });
  const active = state.mcpClients.filter((c) => c.status === "active");
  const inactive = state.mcpClients.filter((c) => c.status !== "active");
  if (active.length === 0 && inactive.length === 0) {
    list.append(
      el(
        "p",
        { className: "empty" },
        "No clients yet. Connect with the server URL above (OAuth discovery), or mint a personal token below. After OAuth, use the connector once so it shows up here.",
      ),
    );
  } else {
    for (const c of active) {
      list.append(mcpClientRow(c));
    }
    for (const c of inactive) {
      list.append(mcpClientRow(c));
    }
  }
  panel.append(list);

  const advanced = el("details", { className: "mcp-advanced" });
  advanced.append(
    el(
      "summary",
      null,
      "Optional: personal access token (stdio bridge / automation)",
    ),
  );
  const advBody = el("div", { className: "mcp-advanced-body" });
  advBody.append(
    el(
      "p",
      { className: "hint" },
      "Use when a client cannot complete browser OAuth. Max 10 active; default TTL 365 days. Token shown once.",
    ),
  );
  if (!scopesEnforce) {
    advBody.append(
      alertBox(
        "info",
        "Scope enforcement is off — scopes are stored but MCP does not reject tools based on them yet.",
      ),
    );
  }

  const form = el("form", { className: "form-stack" });
  const name = el("input", {
    type: "text",
    name: "name",
    required: true,
    placeholder: "e.g. Stdio bridge · laptop",
    maxlength: "128",
    autocomplete: "off",
  }) as HTMLInputElement;
  const days = el("input", {
    type: "number",
    name: "expiresInDays",
    value: "365",
    min: "1",
    max: "3650",
  }) as HTMLInputElement;

  const scopeChecks: Record<PatScope, HTMLInputElement> = {
    read: scopeCheckbox("read", false),
    send: scopeCheckbox("send", false),
    full: scopeCheckbox("full", true),
  };

  const scopeField = el("fieldset", { className: "scope-fieldset" });
  scopeField.append(
    el("legend", null, "Scopes"),
    el(
      "p",
      { className: "hint" },
      "Prefer least privilege. full replaces other scopes.",
    ),
  );
  for (const scope of ["read", "send", "full"] as PatScope[]) {
    const row = el("label", {
      className: "scope-option",
      title: SCOPE_HELP[scope],
    });
    const labelText = el("span", { className: "scope-label" }, scope);
    if (scope === "read") {
      labelText.append(
        el(
          "span",
          {
            className: "scope-tip",
            title:
              "list_drafts and reading draft message bodies are under read (intentional — research PATs can see drafts).",
          },
          " · includes drafts",
        ),
      );
    }
    row.append(scopeChecks[scope], labelText);
    const help = el("span", { className: "scope-help" }, SCOPE_HELP[scope]);
    scopeField.append(el("div", { className: "scope-row" }, row, help));
  }

  const syncExclusive = (changed: PatScope): void => {
    if (changed === "full" && scopeChecks.full.checked) {
      scopeChecks.read.checked = false;
      scopeChecks.send.checked = false;
    } else if (changed !== "full" && scopeChecks[changed].checked) {
      scopeChecks.full.checked = false;
    }
  };
  for (const scope of ["read", "send", "full"] as PatScope[]) {
    scopeChecks[scope].addEventListener("change", () => {
      syncExclusive(scope);
    });
  }

  form.append(
    el(
      "div",
      { className: "form-row" },
      el("label", { className: "field" }, "Client name", name),
      el("label", { className: "field" }, "Expires in days", days),
    ),
    scopeField,
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "mint" ? true : undefined,
        },
        state.busy === "mint" ? "Creating…" : "Create token",
      ),
    ),
  );

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const n = name.value.trim();
    const d = Number(days.value) || 365;
    const scopes = selectedScopes(scopeChecks);
    if (scopes.length === 0) {
      setError("Select at least one scope (read, send, or full)");
      render();
      return;
    }
    void onMint(n, d, scopes, () => {
      name.value = "";
      scopeChecks.read.checked = false;
      scopeChecks.send.checked = false;
      scopeChecks.full.checked = true;
    });
  });

  advBody.append(form);
  advanced.append(advBody);
  panel.append(advanced);

  return panel;
}

function scopeCheckbox(scope: PatScope, checked: boolean): HTMLInputElement {
  return el("input", {
    type: "checkbox",
    name: "scopes",
    value: scope,
    checked: checked ? true : undefined,
  }) as HTMLInputElement;
}

function selectedScopes(
  checks: Record<PatScope, HTMLInputElement>,
): PatScope[] {
  if (checks.full.checked) return ["full"];
  const out: PatScope[] = [];
  if (checks.read.checked) out.push("read");
  if (checks.send.checked) out.push("send");
  return out;
}

function scopeBadges(scopes: PatScope[]): HTMLElement[] {
  return scopes.map((s) => {
    let cls = "badge badge-muted";
    if (s === "full") cls = "badge badge-ok";
    else if (s === "send") cls = "badge badge-warn";
    else if (s === "read") cls = "badge badge-muted";
    const title =
      s === "read"
        ? "read — includes list_drafts and reading draft bodies"
        : SCOPE_HELP[s];
    return el("span", { className: cls, title }, s);
  });
}

function mintedSecretBox(m: MintedPat): HTMLElement {
  const box = el("div", { className: "secret-box" });
  const scopes = scopesOf(m);
  const metaLine = el("div", { className: "list-item-meta" }, `${m.name} · ${m.tokenPrefix}…`);
  for (const b of scopeBadges(scopes)) metaLine.append(b);
  box.append(
    alertBox(
      "warn",
      "Copy this token now. It will not be shown again. Treat it like a password.",
    ),
    metaLine,
    el("code", null, m.token),
    el(
      "div",
      { className: "form-actions" },
      el("button", { type: "button", className: "btn-primary btn-sm" }, "Copy token"),
      el("button", { type: "button", className: "btn-secondary btn-sm" }, "Dismiss"),
    ),
  );
  const [copyBtn, dismissBtn] = box.querySelectorAll("button");
  copyBtn?.addEventListener("click", async () => {
    const ok = await copyText(m.token);
    setNotice(ok ? "Token copied to clipboard" : "Could not copy — select the token manually");
    render();
  });
  dismissBtn?.addEventListener("click", () => {
    state.minted = null;
    setNotice(null);
    render();
  });
  return box;
}

/** Active MCP client row (OAuth or PAT) with disconnect. */
function mcpClientRow(c: McpClient): HTMLElement {
  const row = el("div", { className: "list-item" });
  const revoked = c.status === "revoked";
  const title = el("div", { className: "list-item-title" }, c.name);
  title.append(
    el(
      "span",
      { className: "badge badge-muted" },
      c.kind === "oauth" ? "oauth" : "token",
    ),
  );
  if (revoked) {
    title.append(el("span", { className: "badge badge-err" }, "disconnected"));
  }
  if (c.scopes && c.scopes.length > 0) {
    for (const b of scopeBadges(c.scopes)) title.append(b);
  }
  const metaParts = [
    `created ${formatDate(c.createdAt)}`,
    c.lastUsedAt
      ? `last used ${formatDate(c.lastUsedAt)}`
      : "never used on MCP",
  ];
  if (c.product) metaParts.unshift(c.product);
  row.append(
    el(
      "div",
      { className: "list-item-main" },
      title,
      el("div", { className: "list-item-meta" }, metaParts.join(" · ")),
    ),
  );
  if (!revoked) {
    const actions = el("div", { className: "list-item-actions" });
    const btn = el(
      "button",
      {
        type: "button",
        className: "btn-danger btn-sm",
        disabled: state.busy === `disc-${c.id}` ? true : undefined,
      },
      state.busy === `disc-${c.id}` ? "…" : "Disconnect",
    ) as HTMLButtonElement;
    btn.addEventListener("click", () => {
      const kindLabel = c.kind === "oauth" ? "OAuth client" : "token";
      if (
        !confirm(
          `Disconnect “${c.name}”? This ${kindLabel} will lose MCP access immediately.`,
        )
      ) {
        return;
      }
      void onDisconnectMcpClient(c.id);
    });
    actions.append(btn);
    row.append(actions);
  }
  return row;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

// ── Audit log (own events) ────────────────────────────────────────────────

function formatMeta(meta: Record<string, unknown> | undefined): string {
  if (!meta || Object.keys(meta).length === 0) return "";
  try {
    return JSON.stringify(meta);
  } catch {
    return "";
  }
}

/**
 * Resolve audit accountId to a human label from loaded mail accounts.
 * Prefer displayName + address; fall back to address, then raw id (deleted).
 */
function accountLabelForAudit(accountId: string | undefined): {
  label: string;
  title?: string;
} {
  if (!accountId) return { label: "—" };
  const acct = state.accounts.find((a) => a.id === accountId);
  if (!acct) {
    return { label: accountId, title: `Unknown or removed account (${accountId})` };
  }
  const name = (acct.displayName ?? "").trim();
  if (name) {
    return {
      label: `${name} (${acct.address})`,
      title: `${name} · ${acct.address} · ${accountId}`,
    };
  }
  return { label: acct.address, title: `${acct.address} · ${accountId}` };
}

function auditTableRow(ev: AuditEvent): HTMLElement {
  const tr = el("tr", { className: "audit-tr" });
  const metaStr = formatMeta(ev.meta);
  const account = accountLabelForAudit(ev.accountId);
  tr.append(
    el("td", { className: "audit-td audit-td-time" }, formatDate(ev.at)),
    el("td", { className: "audit-td audit-td-action" }, ev.action),
    el("td", { className: "audit-td audit-td-tool" }, ev.tool ?? "—"),
    el(
      "td",
      {
        className: "audit-td audit-td-account",
        title: account.title,
      },
      account.label,
    ),
    el(
      "td",
      {
        className: "audit-td audit-td-meta",
        title: metaStr || undefined,
      },
      metaStr || "—",
    ),
  );
  return tr;
}

/** Full activity log page — always expanded, no hide/collapse control. */
function auditPanel(): HTMLElement {
  const panel = el("section", { className: "panel audit-panel" });
  const count = state.auditEvents.length;

  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary btn-sm",
      disabled: state.busy === "audit-refresh" ? true : undefined,
    },
    state.busy === "audit-refresh" ? "…" : "Refresh",
  ) as HTMLButtonElement;
  refreshBtn.addEventListener("click", () => {
    void onRefreshAudit();
  });

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Activity log"),
        el(
          "p",
          { className: "sub" },
          count === 0
            ? "Control-plane and MCP events (no message bodies). Kept for 18 months. Newest first."
            : `Your events · ${count}${state.auditNextCursor ? "+" : ""} shown. Kept for 18 months. Newest first.`,
        ),
      ),
      el("div", { className: "panel-header-actions" }, refreshBtn),
    ),
  );

  if (state.auditEvents.length === 0) {
    panel.append(
      el(
        "p",
        { className: "empty" },
        "No audit events yet. Connecting accounts, minting tokens, and MCP tools will appear here.",
      ),
    );
    return panel;
  }

  const scroll = el("div", {
    className: "audit-table-scroll",
    role: "region",
    "aria-label": "Activity log table",
  });
  const table = el("table", { className: "audit-table" });
  const thead = el("thead");
  thead.append(
    el(
      "tr",
      null,
      el("th", { scope: "col" }, "When"),
      el("th", { scope: "col" }, "Action"),
      el("th", { scope: "col" }, "Tool"),
      el("th", { scope: "col" }, "Account"),
      el("th", { scope: "col" }, "Meta"),
    ),
  );
  const tbody = el("tbody");
  for (const ev of state.auditEvents) {
    tbody.append(auditTableRow(ev));
  }
  table.append(thead, tbody);
  scroll.append(table);
  panel.append(scroll);

  if (state.auditNextCursor) {
    const more = el(
      "div",
      { className: "form-actions", style: "margin-top:0.75rem" },
      el(
        "button",
        {
          type: "button",
          className: "btn-secondary btn-sm",
          disabled: state.busy === "audit-more" ? true : undefined,
        },
        state.busy === "audit-more" ? "Loading…" : "Load more",
      ),
    );
    more.querySelector("button")?.addEventListener("click", () => {
      void onLoadMoreAudit();
    });
    panel.append(more);
  }

  return panel;
}

// ── Admin anomaly flags ───────────────────────────────────────────────────

function severityBadge(severity: string): HTMLElement {
  const s = severity.toLowerCase();
  let cls = "badge badge-muted";
  if (s === "high") cls = "badge badge-err";
  else if (s === "medium") cls = "badge badge-warn";
  else if (s === "low") cls = "badge badge-muted";
  return el("span", { className: cls }, severity);
}

function flagStatusBadge(status: string): HTMLElement {
  const s = status.toLowerCase();
  const cls = s === "open" ? "badge badge-warn" : "badge badge-ok";
  return el("span", { className: cls }, status);
}

function adminFlagRow(f: AdminFlag): HTMLElement {
  const row = el("div", { className: "list-item" });
  const title = el("div", { className: "list-item-title" }, f.reason);
  title.append(severityBadge(String(f.severity)));
  title.append(flagStatusBadge(String(f.status)));

  const metaParts = [
    `id ${f.flagId}`,
    formatDate(f.createdAt),
    f.userId ? `user ${f.userId}` : null,
    f.ackedAt ? `acked ${formatDate(f.ackedAt)}` : null,
  ].filter(Boolean) as string[];
  const metaStr = formatMeta(f.meta);
  if (metaStr) metaParts.push(metaStr);

  const actions = el("div", { className: "list-item-actions" });
  if (String(f.status).toLowerCase() === "open") {
    const ackBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled: state.busy === `ack-flag-${f.flagId}` ? true : undefined,
      },
      state.busy === `ack-flag-${f.flagId}` ? "…" : "Ack",
    );
    ackBtn.addEventListener("click", () => {
      void onAckFlag(f.flagId);
    });
    actions.append(ackBtn);
  }

  row.append(
    el(
      "div",
      { className: "list-item-main" },
      title,
      el("div", { className: "list-item-meta" }, metaParts.join(" · ")),
    ),
    actions,
  );
  return row;
}

// ── Admin: join requests + invites + users ────────────────────────────────

function adminJoinRequestsPanel(): HTMLElement {
  const panel = el("section", {
    className: "panel admin-panel",
    id: "admin-join-requests",
  });
  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Admin · Join requests"),
        el(
          "p",
          { className: "sub" },
          "Earlier requests from the old join form. New visitors create an account on the homepage. Approving a pending request still emails an invite.",
        ),
      ),
      el(
        "a",
        {
          href: "/",
          className: "btn-ghost btn-sm",
          target: "_blank",
          rel: "noopener",
        },
        "Open homepage",
      ),
    ),
  );

  // Pending Gmail count banner
  const pendingGmail = state.joinRequests.filter(
    (r) =>
      r.status === "pending" &&
      r.wantsGmail &&
      r.googleTestUserStatus !== "added",
  );
  if (pendingGmail.length > 0) {
    panel.append(
      alertBox(
        "warn",
        `${pendingGmail.length} pending Gmail applicant(s) need Google Test users before Connect Gmail works.`,
      ),
    );
  }

  const filters = el("div", { className: "filter-row" });
  for (const opt of [
    { id: "pending" as const, label: "Pending" },
    { id: "invited" as const, label: "Invited" },
    { id: "dismissed" as const, label: "Dismissed" },
    { id: "all" as const, label: "All" },
  ]) {
    const active = state.joinRequestStatusFilter === opt.id;
    const btn = el(
      "button",
      {
        type: "button",
        className: active ? "btn-primary btn-sm" : "btn-secondary btn-sm",
        disabled: state.busy === "join-refresh" ? true : undefined,
      },
      opt.label,
    );
    btn.addEventListener("click", () => {
      if (state.joinRequestStatusFilter === opt.id) return;
      state.joinRequestStatusFilter = opt.id;
      void onRefreshJoinRequests();
    });
    filters.append(btn);
  }
  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-ghost btn-sm",
      disabled: state.busy === "join-refresh" ? true : undefined,
    },
    state.busy === "join-refresh" ? "…" : "Refresh",
  );
  refreshBtn.addEventListener("click", () => {
    void onRefreshJoinRequests();
  });
  filters.append(refreshBtn);
  panel.append(filters);

  const list = el("div", { className: "list" });
  if (state.joinRequests.length === 0) {
    list.append(
      el("p", { className: "empty" }, "No join requests for this filter."),
    );
  } else {
    for (const row of state.joinRequests) {
      list.append(joinRequestRow(row));
    }
  }
  panel.append(list);
  return panel;
}

function joinRequestRow(row: JoinRequestRow): HTMLElement {
  const elRow = el("div", { className: "list-row" });
  const statusClass =
    row.status === "pending"
      ? "badge badge-warn"
      : row.status === "invited"
        ? "badge badge-ok"
        : "badge badge-muted";
  const wantsGmail = row.wantsGmail === true;
  const gmailStatus = row.googleTestUserStatus || (wantsGmail ? "needed" : "not_needed");
  const gmailBadgeClass =
    gmailStatus === "added"
      ? "badge badge-ok"
      : gmailStatus === "needed" || gmailStatus === "manual"
        ? "badge badge-warn"
        : "badge badge-muted";
  const main = el("div", { className: "list-main" });
  main.append(
    el(
      "div",
      { className: "list-title" },
      row.email,
      " ",
      el("span", { className: statusClass }, row.status),
      wantsGmail
        ? el("span", { className: "badge badge-warn" }, "Gmail")
        : el("span", { className: "badge badge-muted" }, "no Gmail"),
      wantsGmail
        ? el("span", { className: gmailBadgeClass }, `Google: ${gmailStatus}`)
        : null,
      row.name ? el("span", { className: "badge badge-muted" }, row.name) : null,
    ),
    el(
      "div",
      { className: "list-meta" },
      [
        row.company ? row.company : null,
        wantsGmail && row.gmailEmail && row.gmailEmail !== row.email
          ? `Google: ${row.gmailEmail}`
          : null,
        `Requested ${formatDate(row.createdAt)}`,
        row.note
          ? row.note.slice(0, 120) + (row.note.length > 120 ? "…" : "")
          : null,
        row.googleTestUserNote
          ? row.googleTestUserNote.slice(0, 80)
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
    ),
  );
  elRow.append(main);

  const actions = el("div", { className: "row-actions" });
  if (row.status === "pending" && state.features["invites.productized"]) {
    const inviteBtn = el(
      "button",
      {
        type: "button",
        className: "btn-primary btn-sm",
        disabled: state.busy === `join-invite-${row.email}` ? true : undefined,
      },
      state.busy === `join-invite-${row.email}`
        ? "…"
        : wantsGmail
          ? "Approve + invite"
          : "Invite",
    );
    inviteBtn.addEventListener("click", () => {
      void onInviteFromJoinRequest(row);
    });
    actions.append(inviteBtn);
  }
  if (wantsGmail && gmailStatus !== "added") {
    const googleBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled:
          state.busy === `join-google-${row.email}` ? true : undefined,
      },
      state.busy === `join-google-${row.email}` ? "…" : "Google test user",
    );
    googleBtn.addEventListener("click", () => {
      void onAddGoogleTestUser(row.email);
    });
    actions.append(googleBtn);
  }
  if (row.status === "pending") {
    const dismissBtn = el(
      "button",
      {
        type: "button",
        className: "btn-ghost btn-sm",
        disabled: state.busy === `join-dismiss-${row.email}` ? true : undefined,
      },
      "Dismiss",
    );
    dismissBtn.addEventListener("click", () => {
      void onPatchJoinRequest(row.email, { status: "dismissed" });
    });
    actions.append(dismissBtn);
  } else if (row.status === "dismissed") {
    const restoreBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled: state.busy === `join-restore-${row.email}` ? true : undefined,
      },
      "Restore",
    );
    restoreBtn.addEventListener("click", () => {
      void onPatchJoinRequest(row.email, { status: "pending" });
    });
    actions.append(restoreBtn);
  }
  elRow.append(actions);
  return elRow;
}

async function onRefreshJoinRequests(): Promise<void> {
  setBusy("join-refresh");
  setError(null);
  try {
    const status =
      state.joinRequestStatusFilter === "all"
        ? undefined
        : state.joinRequestStatusFilter;
    state.joinRequests = await api.listJoinRequests(
      status ? { status } : undefined,
    );
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onPatchJoinRequest(
  email: string,
  body: {
    status?: "pending" | "invited" | "dismissed";
    googleTestUserStatus?: string;
  },
): Promise<void> {
  const status = body.status;
  const busyKey =
    status === "dismissed"
      ? `join-dismiss-${email}`
      : status === "pending"
        ? `join-restore-${email}`
        : `join-invite-${email}`;
  setBusy(busyKey);
  setError(null);
  try {
    await api.patchJoinRequest(email, body);
    setNotice(
      status === "dismissed"
        ? `Dismissed ${email}`
        : status === "pending"
          ? `Restored ${email}`
          : `Marked ${email} invited`,
    );
    await onRefreshJoinRequests();
  } catch (err) {
    setError(errMessage(err));
    setBusy(null);
    render();
  }
}

/** Best-effort Google OAuth test-user add; always surfaces console URL when needed. */
async function onAddGoogleTestUser(email: string): Promise<void> {
  setBusy(`join-google-${email}`);
  setError(null);
  try {
    const res = await api.addJoinRequestGoogleTestUser(email);
    const g = res.google;
    if (g?.consoleUrl && g.status !== "added") {
      const ok = await copyText(res.gmailEmail || res.email);
      setNotice(
        g.note
          ? `${g.note}${ok ? " Email copied." : ""}`
          : `Add ${res.gmailEmail || res.email} under Google Auth Platform → Audience → Test users.${ok ? " Email copied." : ""}`,
      );
      window.open(g.consoleUrl, "_blank", "noopener,noreferrer");
    } else if (g?.status === "added") {
      setNotice(`Added ${res.gmailEmail || res.email} to Google test users.`);
    } else {
      setNotice(`Google test user status: ${g?.status ?? "unknown"}`);
    }
    await onRefreshJoinRequests();
  } catch (err) {
    setError(errMessage(err));
    setBusy(null);
    render();
  }
}

/**
 * Approve: create productized invite, mark invited, and for Gmail applicants
 * kick off Google test-user enrollment (console deep-link when manual).
 */
async function onInviteFromJoinRequest(row: JoinRequestRow): Promise<void> {
  const email = row.email;
  setBusy(`join-invite-${email}`);
  setError(null);
  try {
    // Gmail first so Connect Gmail works when they land.
    if (row.wantsGmail) {
      try {
        const gRes = await api.addJoinRequestGoogleTestUser(email);
        const g = gRes.google;
        if (g?.consoleUrl && g.status !== "added") {
          await copyText(row.gmailEmail || row.email);
          window.open(g.consoleUrl, "_blank", "noopener,noreferrer");
        }
      } catch {
        // Non-fatal — invite still proceeds.
      }
    }

    const created = await api.createAdminInvite({ email, role: "user" });
    state.lastInviteLink = {
      email: created.email,
      inviteUrl: created.inviteUrl,
    };
    try {
      await api.patchJoinRequest(email, { status: "invited" });
    } catch {
      // Invite may still have succeeded — surface link either way.
    }
    const gmailNote = row.wantsGmail
      ? " Google Test users console opened if still needed — paste the Gmail address under Test users."
      : "";
    setNotice(
      `Invite sent to ${email}.${gmailNote} One-time link shown in Admin · Invites.`,
    );
    state.adminInvites = await api
      .listAdminInvites()
      .catch(() => state.adminInvites);
    const status =
      state.joinRequestStatusFilter === "all"
        ? undefined
        : state.joinRequestStatusFilter;
    state.joinRequests = await api.listJoinRequests(
      status ? { status } : undefined,
    );
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

function filteredAdminInvites(): AdminInvite[] {
  const f = state.adminInviteStatusFilter;
  if (f === "all") return state.adminInvites;
  return state.adminInvites.filter((inv) => inv.status === f);
}

function adminInvitesPanel(): HTMLElement {
  const panel = el("section", {
    className: "panel admin-panel",
    id: "admin-invites",
  });
  const productized = state.features["invites.productized"] === true;
  const isOwner = state.user?.role === "owner";

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Admin · Invites"),
        el(
          "p",
          { className: "sub" },
          "Email an invite link and a temporary password. People can also create an account on the homepage. Copy the invite URL if SES fails.",
        ),
      ),
    ),
  );

  if (!productized) {
    panel.append(
      alertBox(
        "warn",
        "Feature flag invites.productized is off — enable CONFIG#features in DynamoDB to use in-app invites.",
      ),
    );
    return panel;
  }

  if (state.lastInviteLink) {
    const linkBox = el("div", { className: "invite-url-box" });
    linkBox.append(
      el(
        "p",
        { className: "sub" },
        `One-time invite link for ${state.lastInviteLink.email} (shown once — copy now):`,
      ),
    );
    const urlRow = el("div", { className: "invite-url-row" });
    const urlInput = el("input", {
      type: "text",
      className: "input mono",
      readOnly: "true",
      value: state.lastInviteLink.inviteUrl,
    }) as HTMLInputElement;
    const copyBtn = el(
      "button",
      { type: "button", className: "btn-secondary btn-sm" },
      "Copy link",
    );
    copyBtn.addEventListener("click", () => {
      void (async () => {
        const ok = await copyText(state.lastInviteLink!.inviteUrl);
        setNotice(ok ? "Invite link copied." : "Could not copy — select and copy manually.");
        render();
      })();
    });
    const dismissBtn = el(
      "button",
      { type: "button", className: "btn-ghost btn-sm" },
      "Dismiss",
    );
    dismissBtn.addEventListener("click", () => {
      state.lastInviteLink = null;
      render();
    });
    urlRow.append(urlInput, copyBtn, dismissBtn);
    linkBox.append(urlRow);
    panel.append(linkBox);
  }

  // Create form
  const form = el("form", { className: "form admin-invite-form" });
  const email = el("input", {
    type: "email",
    name: "email",
    className: "input",
    placeholder: "tester@example.com",
    required: "true",
    autocomplete: "off",
  }) as HTMLInputElement;
  const role = el("select", {
    name: "role",
    className: "input",
  }) as HTMLSelectElement;
  role.append(
    el("option", { value: "user" }, "user"),
    ...(isOwner ? [el("option", { value: "admin" }, "admin")] : []),
  );
  form.append(
    el(
      "div",
      { className: "form-row" },
      el("label", null, "Email", email),
      el("label", null, "Role", role),
    ),
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "invite-create" ? true : undefined,
        },
        state.busy === "invite-create" ? "Sending…" : "Send invite",
      ),
    ),
  );
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onCreateInvite(email.value.trim(), role.value as "user" | "admin");
  });
  panel.append(form);

  // Status filters
  const filters = el("div", { className: "filter-row" });
  for (const opt of [
    { id: "pending" as const, label: "Pending" },
    { id: "accepted" as const, label: "Accepted" },
    { id: "revoked" as const, label: "Revoked" },
    { id: "all" as const, label: "All" },
  ]) {
    const active = state.adminInviteStatusFilter === opt.id;
    const btn = el(
      "button",
      {
        type: "button",
        className: active ? "btn-primary btn-sm" : "btn-secondary btn-sm",
        disabled: state.busy === "invites-refresh" ? true : undefined,
      },
      opt.label,
    );
    btn.addEventListener("click", () => {
      if (state.adminInviteStatusFilter === opt.id) return;
      state.adminInviteStatusFilter = opt.id;
      render();
    });
    filters.append(btn);
  }
  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-ghost btn-sm",
      disabled: state.busy === "invites-refresh" ? true : undefined,
    },
    state.busy === "invites-refresh" ? "…" : "Refresh",
  );
  refreshBtn.addEventListener("click", () => {
    void onRefreshAdminInvites();
  });
  filters.append(refreshBtn);
  panel.append(filters);

  const list = el("div", { className: "list" });
  const rows = filteredAdminInvites();
  if (rows.length === 0) {
    list.append(el("p", { className: "empty" }, "No invites match this filter."));
  } else {
    for (const inv of rows) {
      list.append(adminInviteRow(inv));
    }
  }
  panel.append(list);
  return panel;
}

function adminInviteRow(inv: AdminInvite): HTMLElement {
  const row = el("div", { className: "list-row admin-invite-row" });
  const pending = inv.status === "pending";
  const meta = el("div", { className: "list-main" });
  const statusBadgeClass =
    inv.status === "pending"
      ? "badge badge-warn"
      : inv.status === "accepted"
        ? "badge badge-ok"
        : inv.status === "revoked" || inv.status === "expired"
          ? "badge badge-err"
          : "badge badge-muted";
  meta.append(
    el(
      "div",
      { className: "list-title" },
      inv.email,
      " ",
      el("span", { className: statusBadgeClass }, inv.status),
      " ",
      el("span", { className: "badge badge-muted" }, inv.role),
    ),
    el(
      "div",
      { className: "list-meta" },
      `Created ${formatDate(inv.createdAt)} · expires ${formatDate(inv.expiresAt)}`,
      inv.resendCount > 0 ? ` · resent ×${inv.resendCount}` : "",
      inv.acceptedAt ? ` · accepted ${formatDate(inv.acceptedAt)}` : "",
    ),
  );
  row.append(meta);

  if (pending) {
    const actions = el("div", { className: "row-actions" });
    const resendBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled: state.busy === `invite-resend-${inv.id}` ? true : undefined,
      },
      state.busy === `invite-resend-${inv.id}` ? "…" : "Resend",
    );
    resendBtn.addEventListener("click", () => {
      void onResendInvite(inv.id);
    });
    const revokeBtn = el(
      "button",
      {
        type: "button",
        className: "btn-danger btn-sm",
        disabled: state.busy === `invite-revoke-${inv.id}` ? true : undefined,
      },
      state.busy === `invite-revoke-${inv.id}` ? "…" : "Revoke",
    );
    revokeBtn.addEventListener("click", () => {
      if (
        !window.confirm(
          `Revoke invite for ${inv.email}? They will not be able to accept this link.`,
        )
      ) {
        return;
      }
      void onRevokeInvite(inv.id);
    });
    actions.append(resendBtn, revokeBtn);
    row.append(actions);
  }
  return row;
}

function adminUsersPanel(): HTMLElement {
  const panel = el("section", {
    className: "panel admin-panel",
    id: "admin-users",
  });
  const isOwner = state.user?.role === "owner";

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Admin · Users"),
        el(
          "p",
          { className: "sub" },
          "Disable accounts, revoke all PATs, or (owner) change roles. Last owner cannot be demoted/disabled.",
        ),
      ),
      el(
        "button",
        {
          type: "button",
          className: "btn-ghost btn-sm",
          disabled: state.busy === "users-refresh" ? true : undefined,
        },
        state.busy === "users-refresh" ? "…" : "Refresh",
      ),
    ),
  );
  panel
    .querySelector(".panel-header button")
    ?.addEventListener("click", () => {
      void onRefreshAdminUsers();
    });

  const list = el("div", { className: "list" });
  if (state.adminUsers.length === 0) {
    list.append(el("p", { className: "empty" }, "No users yet."));
  } else {
    for (const u of state.adminUsers) {
      list.append(adminUserRow(u, isOwner));
    }
  }
  panel.append(list);
  return panel;
}

function adminUserRow(u: AdminUser, isOwner: boolean): HTMLElement {
  const row = el("div", { className: "list-row admin-user-row" });
  const self = state.user?.userId === u.userId;
  const active = u.status === "active";

  const meta = el("div", { className: "list-main" });
  meta.append(
    el(
      "div",
      { className: "list-title" },
      u.email,
      self ? el("span", { className: "badge" }, "you") : null,
      " ",
      el(
        "span",
        {
          className: active ? "badge badge-ok" : "badge badge-err",
        },
        u.status,
      ),
      " ",
      el("span", { className: "badge badge-muted" }, u.role),
    ),
    el(
      "div",
      { className: "list-meta" },
      `Joined ${formatDate(u.createdAt)} · ${u.userId.slice(0, 12)}…`,
    ),
  );
  row.append(meta);

  const actions = el("div", { className: "row-actions" });

  if (isOwner) {
    const roleSelect = el("select", {
      className: "input input-sm",
      disabled: state.busy === `user-role-${u.userId}` ? true : undefined,
    }) as HTMLSelectElement;
    for (const r of ["owner", "admin", "user"] as UserRole[]) {
      const opt = el("option", { value: r }, r) as HTMLOptionElement;
      if (r === u.role) opt.selected = true;
      roleSelect.append(opt);
    }
    roleSelect.addEventListener("change", () => {
      const next = roleSelect.value as UserRole;
      if (next === u.role) return;
      if (
        !window.confirm(`Change role for ${u.email} to ${next}?`)
      ) {
        roleSelect.value = u.role;
        return;
      }
      void onPatchUserRole(u.userId, next);
    });
    actions.append(roleSelect);
  }

  const patsBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary btn-sm",
      disabled: state.busy === `user-pats-${u.userId}` ? true : undefined,
    },
    state.busy === `user-pats-${u.userId}` ? "…" : "Revoke PATs",
  );
  patsBtn.addEventListener("click", () => {
    if (!window.confirm(`Revoke all PATs for ${u.email}?`)) return;
    void onRevokeUserPats(u.userId);
  });
  actions.append(patsBtn);

  if (active) {
    const disBtn = el(
      "button",
      {
        type: "button",
        className: "btn-danger btn-sm",
        disabled:
          self || state.busy === `user-disable-${u.userId}` ? true : undefined,
      },
      state.busy === `user-disable-${u.userId}` ? "…" : "Disable",
    );
    disBtn.addEventListener("click", () => {
      if (
        !window.confirm(
          `Disable ${u.email}? They lose API/MCP access until re-enabled.`,
        )
      ) {
        return;
      }
      void onDisableUser(u.userId);
    });
    actions.append(disBtn);
  } else {
    const enBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled: state.busy === `user-enable-${u.userId}` ? true : undefined,
      },
      state.busy === `user-enable-${u.userId}` ? "…" : "Enable",
    );
    enBtn.addEventListener("click", () => {
      void onEnableUser(u.userId);
    });
    actions.append(enBtn);
  }

  row.append(actions);
  return row;
}

async function onCreateInvite(
  email: string,
  role: "user" | "admin",
): Promise<void> {
  if (!email) {
    setError("Email is required");
    render();
    return;
  }
  setBusy("invite-create");
  setError(null);
  try {
    const created = await api.createAdminInvite({ email, role });
    state.lastInviteLink = {
      email: created.email,
      inviteUrl: created.inviteUrl,
    };
    const ses = created.emailDelivery?.ses ?? "unknown";
    setNotice(
      `Invite created for ${created.email} (SES: ${ses}). Copy the one-time link below if needed.`,
    );
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onResendInvite(inviteId: string): Promise<void> {
  setBusy(`invite-resend-${inviteId}`);
  setError(null);
  try {
    const created = await api.resendAdminInvite(inviteId);
    state.lastInviteLink = {
      email: created.email,
      inviteUrl: created.inviteUrl,
    };
    setNotice(`Invite resent to ${created.email}. New link shown once below.`);
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRevokeInvite(inviteId: string): Promise<void> {
  setBusy(`invite-revoke-${inviteId}`);
  setError(null);
  try {
    await api.revokeAdminInvite(inviteId);
    setNotice("Invite revoked.");
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRefreshAdminInvites(): Promise<void> {
  setBusy("invites-refresh");
  setError(null);
  try {
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRefreshAdminUsers(): Promise<void> {
  setBusy("users-refresh");
  setError(null);
  try {
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onDisableUser(userId: string): Promise<void> {
  setBusy(`user-disable-${userId}`);
  setError(null);
  try {
    await api.disableAdminUser(userId);
    setNotice("User disabled.");
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onEnableUser(userId: string): Promise<void> {
  setBusy(`user-enable-${userId}`);
  setError(null);
  try {
    await api.enableAdminUser(userId);
    setNotice("User enabled.");
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRevokeUserPats(userId: string): Promise<void> {
  setBusy(`user-pats-${userId}`);
  setError(null);
  try {
    const res = await api.revokeAdminUserPats(userId);
    setNotice(`PATs revoked (tokenVersion ${res.tokenVersion}).`);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onPatchUserRole(userId: string, role: UserRole): Promise<void> {
  setBusy(`user-role-${userId}`);
  setError(null);
  try {
    await api.patchAdminUserRole(userId, role);
    setNotice(`Role updated to ${role}.`);
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

function adminFlagsPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  const anomalyOn = state.features["abuse.anomalyFlags"] === true;

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Anomaly flags"),
        el(
          "p",
          { className: "sub" },
          "Admin-only abuse signals (rate denials when abuse.anomalyFlags is on). Ack after review.",
        ),
      ),
    ),
  );

  if (!anomalyOn) {
    panel.append(
      alertBox(
        "info",
        "Feature flag abuse.anomalyFlags is off — new flags are not recorded. Existing open flags still list here.",
      ),
    );
  }

  // Status filter chips
  const filters = el("div", { className: "filter-row" });
  for (const opt of [
    { id: "open" as const, label: "Open" },
    { id: "acked" as const, label: "Acked" },
    { id: "all" as const, label: "All" },
  ]) {
    const active = state.adminFlagsStatusFilter === opt.id;
    const btn = el(
      "button",
      {
        type: "button",
        className: active ? "btn-primary btn-sm" : "btn-secondary btn-sm",
        disabled: state.busy === "flags-refresh" ? true : undefined,
      },
      opt.label,
    );
    btn.addEventListener("click", () => {
      if (state.adminFlagsStatusFilter === opt.id) return;
      state.adminFlagsStatusFilter = opt.id;
      void onRefreshFlags();
    });
    filters.append(btn);
  }
  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-ghost btn-sm",
      disabled: state.busy === "flags-refresh" ? true : undefined,
    },
    state.busy === "flags-refresh" ? "…" : "Refresh",
  );
  refreshBtn.addEventListener("click", () => {
    void onRefreshFlags();
  });
  filters.append(refreshBtn);
  panel.append(filters);

  const list = el("div", { className: "list" });
  if (state.adminFlags.length === 0) {
    list.append(el("p", { className: "empty" }, "No flags match this filter."));
  } else {
    for (const f of state.adminFlags) {
      list.append(adminFlagRow(f));
    }
  }
  panel.append(list);

  if (state.adminFlagsNextCursor) {
    const more = el(
      "div",
      { className: "form-actions", style: "margin-top:0.75rem" },
      el(
        "button",
        {
          type: "button",
          className: "btn-secondary btn-sm",
          disabled: state.busy === "flags-more" ? true : undefined,
        },
        state.busy === "flags-more" ? "Loading…" : "Load more",
      ),
    );
    more.querySelector("button")?.addEventListener("click", () => {
      void onLoadMoreFlags();
    });
    panel.append(more);
  }

  return panel;
}

// ── Actions ───────────────────────────────────────────────────────────────

async function handleLoginResult(
  res: Awaited<ReturnType<typeof api.login>>,
  email: string,
): Promise<void> {
  if (isLoginChallenge(res)) {
    const name = res.challenge.name;
    if (name === "NEW_PASSWORD_REQUIRED") {
      state.challenge = {
        kind: "NEW_PASSWORD_REQUIRED",
        session: res.challenge.session,
        email,
      };
      setError(null);
      return;
    }
    if (name === "SOFTWARE_TOKEN_MFA") {
      state.challenge = {
        kind: "SOFTWARE_TOKEN_MFA",
        session: res.challenge.session,
        email,
      };
      setError(null);
      return;
    }
    setError(`Unsupported challenge: ${name}`);
    return;
  }

  state.challenge = null;
  state.signup = null;
  state.user = res.user;
  setError(null);
  setNotice(null);
  try {
    // Bind invite after session is established (JWT sub + email match).
    if (state.invite.token || readSessionInviteToken()) {
      await tryAcceptPendingInvite();
    }
    await refreshDashboard();
  } catch (err) {
    setError(errMessage(err));
  }
}

async function onForgotPasswordRequest(email: string): Promise<void> {
  if (!email) {
    setError("Email is required");
    render();
    return;
  }
  setBusy("forgot");
  setError(null);
  try {
    const res = await api.forgotPassword(email);
    if (res.recovery === "temporary_password") {
      // FORCE_CHANGE_PASSWORD: email has a temp password for the Sign in form,
      // not a verification code. Do not open the code-confirm UI.
      state.forgotPassword = null;
      setNotice(
        res.message ??
          "We emailed a temporary password. Sign in with it on this page (not the code form), then set a permanent password.",
      );
    } else {
      state.forgotPassword = { step: "confirm", email };
      setNotice(
        res.message ??
          "If an account exists for that email, we sent a verification code.",
      );
    }
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onForgotPasswordConfirm(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  if (!email || !code || !newPassword) {
    setError("Email, code, and new password are required");
    render();
    return;
  }
  setBusy("forgot");
  setError(null);
  try {
    const res = await api.confirmForgotPassword({ email, code, newPassword });
    state.forgotPassword = null;
    setNotice(
      res.message ?? "Password updated. Sign in with your new password.",
    );
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onLogin(email: string, password: string): Promise<void> {
  if (!email || !password) {
    setError("Email and password are required");
    render();
    return;
  }
  setBusy("login");
  setError(null);
  try {
    const res = await api.login(email, password);
    await handleLoginResult(res, email);
  } catch (err) {
    if (err instanceof ApiError && err.code === "user_not_confirmed") {
      state.signup = { step: "confirm", email };
      state.forgotPassword = null;
      state.challenge = null;
      setError(null);
      try {
        await api.resendSignup(email);
        setNotice(
          "Confirm your email before signing in. We sent a new code.",
        );
      } catch (resendErr) {
        setError(errMessage(resendErr));
      }
    } else {
      setError(errMessage(err));
    }
  } finally {
    setBusy(null);
  }
}

async function onSignup(
  email: string,
  password: string,
  confirm: string,
): Promise<void> {
  if (!email || !password) {
    setError("Email and password are required");
    render();
    return;
  }
  if (password !== confirm) {
    setError("Passwords do not match");
    render();
    return;
  }
  if (!passwordMeetsPolicy(password)) {
    setError(SIGNUP_PASSWORD_RULE);
    render();
    return;
  }
  setBusy("signup");
  setError(null);
  try {
    const res = await api.signup(email, password);
    state.signup = {
      step: "confirm",
      email: res.email || email.trim().toLowerCase(),
    };
    setNotice(
      "We emailed a confirmation code. Enter it to finish creating the account.",
    );
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onConfirmSignup(email: string, code: string): Promise<void> {
  if (!email || !code) {
    setError("Email and confirmation code are required");
    render();
    return;
  }
  setBusy("signup");
  setError(null);
  try {
    const res = await api.confirmSignup({ email, code });
    state.signup = null;
    setNotice(res.message ?? "Email confirmed. Sign in with your password.");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onResendSignup(email: string): Promise<void> {
  setBusy("signup-resend");
  setError(null);
  try {
    await api.resendSignup(email);
    setNotice("We sent another confirmation code.");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onNewPassword(
  email: string,
  session: string,
  newPassword: string,
): Promise<void> {
  setBusy("login");
  setError(null);
  try {
    const res = await api.respondNewPassword({ session, email, newPassword });
    await handleLoginResult(res, email);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onTotp(
  email: string,
  session: string,
  totpCode: string,
): Promise<void> {
  setBusy("login");
  setError(null);
  try {
    const res = await api.respondTotp({ session, email, totpCode });
    await handleLoginResult(res, email);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onDeleteOwnAccount(confirmEmail: string): Promise<void> {
  setBusy("delete-me");
  setError(null);
  setNotice(null);
  try {
    await api.deleteOwnAccount(confirmEmail);
    state.user = null;
    state.accounts = [];
    state.selectedAccountId = null;
    state.tokens = [];
    state.mcpClients = [];
    state.minted = null;
    state.features = { ...DEFAULT_FEATURE_FLAGS };
    state.auditEvents = [];
    state.auditNextCursor = null;
    state.auditExpanded = false;
    state.accountDialog = null;
    state.adminFlags = [];
    state.adminFlagsNextCursor = null;
    state.adminFlagsStatusFilter = "open";
    state.challenge = null;
    state.profileMenuOpen = false;
    state.busy = null;
    state.error = null;
    state.notice = "Your account has been deleted.";
    navigateTo("/");
  } catch (err) {
    if (err instanceof ApiError && err.code === "delete_incomplete") {
      state.accounts = [];
      state.tokens = [];
      state.mcpClients = [];
      state.minted = null;
    }
    setError(errMessage(err));
    setBusy(null);
  }
}

async function onLogout(): Promise<void> {
  setBusy("logout");
  try {
    await api.logout();
  } catch {
    // Logout is idempotent; clear local state anyway
  } finally {
    state.user = null;
    state.accounts = [];
    state.selectedAccountId = null;
    state.tokens = [];
    state.mcpClients = [];
    state.minted = null;
    state.features = { ...DEFAULT_FEATURE_FLAGS };
    state.auditEvents = [];
    state.auditNextCursor = null;
    state.auditExpanded = false;
    state.accountDialog = null;
    if (typeof window !== "undefined" && isAdminPath()) {
      window.history.replaceState({}, "", "/");
    }
    state.adminFlags = [];
    state.adminFlagsNextCursor = null;
    state.adminFlagsStatusFilter = "open";
    state.challenge = null;
    setBusy(null);
    setError(null);
    setNotice(null);
  }
}

async function onConnectGmail(
  accountId?: string,
  googleServices?: {
    contacts?: boolean;
    calendar?: boolean;
    settings?: boolean;
    drive?: boolean;
    docs?: boolean;
    sheets?: boolean;
    slides?: boolean;
    tasks?: boolean;
    meet?: boolean;
  },
): Promise<void> {
  const busyKey = accountId ? `gmail-re-${accountId}` : "gmail-start";
  setBusy(busyKey);
  setError(null);
  setNotice(null);
  try {
    const res = await api.startGmailOauth({
      ...(accountId ? { accountId } : {}),
      ...(googleServices ? { googleServices } : {}),
    });
    if (!res.authorizeUrl) {
      setError("Gmail OAuth start did not return authorizeUrl");
      setBusy(null);
      return;
    }
    // Full-page redirect to Google consent (leaves SPA).
    window.location.assign(res.authorizeUrl);
    // Keep busy state until navigation completes.
  } catch (err) {
    setError(errMessage(err));
    setBusy(null);
  }
}

async function onUpdateAccount(
  id: string,
  body: api.UpdateAccountBody,
): Promise<void> {
  setBusy(`edit-${id}`);
  setError(null);
  setNotice(null);
  try {
    const updated = await api.updateAccount(id, body);
    state.accounts = state.accounts.map((a) =>
      a.id === id ? { ...a, ...updated } : a,
    );
    if (updated.reconnectRequired) {
      setNotice(
        updated.reconnectHint ??
          "Saved. Apply with Google to authorize new services.",
      );
    } else {
      setNotice(`Updated ${updated.address}`);
    }
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onAddImap(
  body: api.CreateImapBody,
  clearForm: () => void,
): Promise<void> {
  setBusy("add-imap");
  setError(null);
  setNotice(null);
  try {
    // Intentionally do not log body (contains appPassword).
    const created = await api.createImapAccount(body);
    state.accounts = [created, ...state.accounts.filter((a) => a.id !== created.id)];
    clearForm();
    state.accountDialog = null;
    setNotice(`Connected ${created.address}`);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onDeleteAccount(id: string): Promise<void> {
  setBusy(`del-acc-${id}`);
  setError(null);
  try {
    await api.deleteAccount(id);
    state.accounts = state.accounts.filter((a) => a.id !== id);
    if (state.selectedAccountId === id) state.selectedAccountId = null;
    setNotice("Account disconnected");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onMint(
  name: string,
  expiresInDays: number,
  scopes: PatScope[],
  clearForm: () => void,
): Promise<void> {
  if (!name) {
    setError("Token name is required");
    render();
    return;
  }
  if (scopes.length === 0) {
    setError("Select at least one scope (read, send, or full)");
    render();
    return;
  }
  setBusy("mint");
  setError(null);
  setNotice(null);
  try {
    const minted = await api.mintToken({ name, expiresInDays, scopes });
    // Prefer server scopes; fall back to what we requested if older server omits them.
    const effectiveScopes =
      Array.isArray(minted.scopes) && minted.scopes.length > 0
        ? scopesOf(minted)
        : scopes;
    state.minted = { ...minted, scopes: effectiveScopes };
    // Add metadata to list (without raw token)
    const meta: PatMeta = {
      id: minted.id,
      name: minted.name,
      tokenPrefix: minted.tokenPrefix,
      createdAt: minted.createdAt,
      expiresAt: minted.expiresAt,
      scopes: effectiveScopes,
    };
    state.tokens = [meta, ...state.tokens.filter((t) => t.id !== meta.id)];
    // Minted PATs show in Active clients via merge (pat_{id}) even before first /mcp use.
    const patClientId = `pat_${minted.id}`;
    const patClient: McpClient = {
      id: patClientId,
      kind: "pat",
      name: minted.name,
      status: "active",
      createdAt: minted.createdAt,
      lastUsedAt: minted.createdAt,
      scopes: effectiveScopes,
      patTokenId: minted.id,
    };
    state.mcpClients = [
      patClient,
      ...state.mcpClients.filter((c) => c.id !== patClientId),
    ];
    clearForm();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onDisconnectMcpClient(clientId: string): Promise<void> {
  setBusy(`disc-${clientId}`);
  setError(null);
  try {
    await api.disconnectMcpClient(clientId);
    const wasPat = clientId.startsWith("pat_");
    const patTokenId = wasPat ? clientId.slice("pat_".length) : undefined;
    state.mcpClients = state.mcpClients.map((c) =>
      c.id === clientId ? { ...c, status: "revoked" as const } : c,
    );
    // Drop revoked PATs from the tokens list and minted secret if present.
    if (patTokenId) {
      state.tokens = state.tokens.filter((t) => t.id !== patTokenId);
      if (state.minted?.id === patTokenId) state.minted = null;
      // Remove PAT row entirely (revoked tokens are deleted server-side).
      state.mcpClients = state.mcpClients.filter((c) => c.id !== clientId);
    }
    setNotice("Client disconnected");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onRefreshAudit(): Promise<void> {
  setBusy("audit-refresh");
  setError(null);
  try {
    const page = await api.listAudit({ limit: AUDIT_PAGE_SIZE });
    state.auditEvents = page.events;
    state.auditNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onLoadMoreAudit(): Promise<void> {
  if (!state.auditNextCursor) return;
  setBusy("audit-more");
  setError(null);
  try {
    const page = await api.listAudit({
      limit: AUDIT_PAGE_SIZE,
      cursor: state.auditNextCursor,
    });
    const seen = new Set(state.auditEvents.map((e) => e.id));
    for (const ev of page.events) {
      if (!seen.has(ev.id)) {
        state.auditEvents.push(ev);
        seen.add(ev.id);
      }
    }
    state.auditNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onRefreshFlags(): Promise<void> {
  if (!state.user || !isAdminRole(state.user.role)) return;
  setBusy("flags-refresh");
  setError(null);
  try {
    const status =
      state.adminFlagsStatusFilter === "all"
        ? undefined
        : state.adminFlagsStatusFilter;
    const page = await api.listAdminFlags({
      limit: FLAGS_PAGE_SIZE,
      ...(status ? { status } : {}),
    });
    state.adminFlags = page.flags;
    state.adminFlagsNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onLoadMoreFlags(): Promise<void> {
  if (!state.adminFlagsNextCursor) return;
  if (!state.user || !isAdminRole(state.user.role)) return;
  setBusy("flags-more");
  setError(null);
  try {
    const status =
      state.adminFlagsStatusFilter === "all"
        ? undefined
        : state.adminFlagsStatusFilter;
    const page = await api.listAdminFlags({
      limit: FLAGS_PAGE_SIZE,
      cursor: state.adminFlagsNextCursor,
      ...(status ? { status } : {}),
    });
    const seen = new Set(state.adminFlags.map((f) => f.flagId));
    for (const f of page.flags) {
      if (!seen.has(f.flagId)) {
        state.adminFlags.push(f);
        seen.add(f.flagId);
      }
    }
    state.adminFlagsNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onAckFlag(flagId: string): Promise<void> {
  setBusy(`ack-flag-${flagId}`);
  setError(null);
  try {
    const updated = await api.ackAdminFlag(flagId);
    state.adminFlags = state.adminFlags.map((f) =>
      f.flagId === flagId ? updated : f,
    );
    // If filtering open only, drop acked from list.
    if (state.adminFlagsStatusFilter === "open") {
      state.adminFlags = state.adminFlags.filter(
        (f) => String(f.status).toLowerCase() === "open",
      );
    }
    setNotice(`Flag ${flagId} acknowledged`);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}
